import { type PolygonOptions, type PolylineOptions } from '@2gis/mapgl/types'

import regionBoundaries from './regionBoundaries.json'
import {
  LONGEST_BUS_ROUTE,
  LONGEST_TRAM_ROUTE,
  LONGEST_TROLLEYBUS_ROUTE,
} from './transportRoutes'

const createMapPolygons = (coordinates: number[][][][]): PolygonOptions[] =>
  coordinates.map(polygonCoordinates => ({
    color: '#19AA1E33',
    coordinates: polygonCoordinates,
    strokeColor: '#19AA1E',
    strokeWidth: 3,
  }))

export const steps = [
  {
    name: 'Самое фотографируемое здание',
    tag: 'Здание',
    title: 'Самое фотографируемое здание',
    description: (
      <>
        В карточке Исаакиевского собора — более 10 000 фотографий. Это больше,
        чем у любого другого российского объекта в 2ГИС. Сам же Исаакиевский
        собор — самый большой храм Петербурга. Он может вместить 12 000 человек.
        <br />
        <br />
        Откуда фотографировать Исаакиевский? Самый открыточный вариант вблизи —
        из Исаакиевского сквера. Ничего лишнего в кадре, здание обрамляют цветы
        и зелень. Портреты на фоне Исаакия (и посиделки на траве) — в
        Александровском саду. Фото собора издалека, в «небесной линии
        Петербурга» — с Университетской набережной.
        <br />
        <br />А с колоннады самого Исаакиевского собора — роскошный вид на
        город.
      </>
    ),
    description_mob: (
      <>
        В карточке Исаакиевского собора — более 10 000 фотографий. Это больше,
        чем у любого другого российского объекта в 2ГИС. Сам же Исаакиевский
        собор — самый большой храм Петербурга. Он может вместить 12 000 человек.
      </>
    ),
    address: 'Исаакиевский собор в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/0t74axy1',
    map: {
      center: [30.305838, 59.933979],
      zoom: 17.86,
    },
    selectObjects: ['5348660212672137'],
    markerCords: [30.30586807500977, 59.934048571406095],
  },
  {
    name: 'Самая популярная достопримечательность',
    tag: 'Место',
    title: 'Самая популярная достопримечательность',
    description: (
      <>
        Из всех российских достопримечательностей пользователи 2ГИС чаще всего
        взаимодействуют на картах именно с Эрмитажем. Изучают его карточку,
        строят к нему маршруты, оставляют отзывы. Сам Эрмитаж в 2025 году
        насчитал 3,8 млн посетителей.
        <br />
        <br />
        Туристов влечёт к Эрмитажу не только желание увидеть «Возвращение
        блудного сына», но и само здание Зимнего дворца: бывшая резиденция
        российских императоров, шедевр Бартоломео Растрелли, образец барокко
        середины XVIII века.
      </>
    ),
    description_mob: (
      <>
        Из всех российских достопримечательностей пользователи 2ГИС чаще всего
        взаимодействуют на картах именно с Эрмитажем. Изучают его карточку,
        строят к нему маршруты, оставляют отзывы. Сам Эрмитаж в 2025 году
        насчитал 3,8 млн посетителей.
      </>
    ),
    address: 'Эрмитаж в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/zhjw5sr7',
    map: {
      center: [30.313822, 59.940209],
      zoom: 17.66,
    },
    selectObjects: ['5348660212679008'],
    markerCords: [30.313820856127677, 59.94027826900585],
  },
  {
    name: 'Самый интересный парк',
    tag: 'Место',
    title: 'Самый интересный парк',
    description: (
      <>
        В 2026 году в карточках парков и баз отдыха 2ГИС появилась вкладка со
        всеми организациями на территории. Среди всех парков России больше всего
        организаций — около тысячи — нашлось на территории ВДНХ.
        <br />
        <br />
        Среди них — самое большое колесо обозрения в Европе, мультимедийный
        «СоюзМультПарк», макет Москвы, городская ферма с козами и осликами,
        кафе, аттракционы, магазины… Зимой ко всему этому добавляется ещё и
        самый большой каток столицы.
      </>
    ),
    description_mob: (
      <>
        В 2026 году в карточках парков и баз отдыха 2ГИС появилась вкладка со
        всеми организациями на территории. Среди всех парков России больше всего
        организаций — около тысячи — нашлось на территории ВДНХ.
      </>
    ),
    address: 'ВДНХ в Москве',
    href: 'https://2gis.onelink.me/nw2S/txy6ht5b',
    map: {
      center: [37.63764, 55.826141],
      zoom: 18.41,
    },
    selectObjects: ['4504235282638122'],
    markerCords: [37.63765243281392, 55.82621511571091],
  },
  {
    name: 'Самая ресторанная улица',
    tag: 'Место',
    title: 'Самая ресторанная улица',
    description: (
      <>
        Самая богатая барами и ресторанами улица в России — московская
        Маросейка. На 466-метровой улице нашлось место 38 заведениям. То есть, —
        около 8 заведений на 100 метров: от корейского стритфуда до культовых
        хот-догов.
        <br />
        <br />
        Поесть в этих местах любили всегда — судя хотя бы по тому, что храм
        Святителя Николая XVII века изначально назывался «Церковь Николы в
        Блинниках». Любителям истории и архитектуры старинная Маросейка вообще
        понравится не меньше, чем искателям гастрономических впечатлений.
      </>
    ),
    description_mob: (
      <>
        Самая богатая барами и ресторанами улица в России — московская
        Маросейка. На 466-метровой улице нашлось место 38 заведениям. То есть, —
        около 8 заведений на 100 метров: от корейского стритфуда до культовых
        хот-догов.
      </>
    ),
    address: 'Маросейка в Москве',
    href: 'https://2gis.onelink.me/nw2S/90vqa0wi',
    map: {
      center: [37.635169282487524, 55.757660063751324],
      zoom: 16.858864257972485,
    },
    selectObjects: ['4504338361746538'],
    markerCords: [37.635169282487524, 55.757660063751324],
  },
  {
    name: 'Самое культурное место',
    tag: 'Место',
    title: 'Самое культурное место',
    description: (
      <>
        Длина 4-го Сыромятнического переулка — всего 250 метров, но по этому
        адресу работает с десяток художественных галерей. И это самая высокая их
        концентрация в России.
        <br />
        <br />
        Такая жизнь в промзоне возле Курского вокзала началась в 2007 году.
        Тогда в стенах пивоваренных цехов XIX века (в советское время ставших
        винным заводом) открылся первый в России центр современного искусства —
        Винзавод.
        <br />
        <br />
        Самая высокая концентрация музеев в России — 10 музеев на 800 метров —
        на набережной Петра Великого в Калининграде. Все они — подразделения
        Музея Мирового океана, обитающего в огромном стеклянном шаре. Половина
        этих музеев — пришвартованные к набережной суда.
        <br />
        <br />
        Следующий кандидат на роль самого культурного места страны — Красная
        площадь в Тобольске. Такой адрес у 10 музеев: от музея сибирской каторги
        до музея забытых звуков. Выбрать из них самое интересное поможет Гид
        2ГИС по Тобольску.
      </>
    ),
    description_mob: (
      <>
        Длина 4-го Сыромятнического переулка — всего 250 метров, но по этому
        адресу работает с десяток художественных галерей. И это самая высокая их
        концентрация в России. Такая жизнь в промзоне возле Курского вокзала
        началась в 2007 году, когда открылся первый в России центр современного
        искусства — Винзавод.
      </>
    ),
    address: '4-й Сыромятнический переулок Москвы',
    href: 'https://2gis.onelink.me/nw2S/k4xv7cak',
    map: {
      center: [37.66621249657656, 55.75502930037558],
      zoom: 17.5,
    },
    selectObjects: ['4504338361748364'],
    markerCords: [37.66621249657656, 55.75502930037558],
  },
  {
    name: 'Самая длинная велосипедная трасса',
    tag: 'Транспорт',
    title: 'Самая длинная велосипедная трасса',
    description: (
      <>
        56-километровый велосипедный маршрут от города Яхрома до города Дубна
        вдоль канала имени Москвы — и так рекордно длинный для России. Но это —
        только первая очередь запланированного 1000-километрового маршрута
        ВЕЛО1, который должен связать Москву и Санкт-Петербург.
        <br />
        <br />
        Это будет велосипедное путешествие на пару недель. А пока есть
        велосипедный путь из Яхромы в Дубну, с которым любитель управится за
        пять часов, а спортсмен — за пару.
        <br />
        <br />
        Путеводители на сайте проекта ВЕЛО1 подскажут, что посмотреть по пути в
        уютной холмистой Яхроме, уникальном наукограде Дубне и старинном
        Дмитрове.
      </>
    ),
    description_mob: (
      <>
        56-километровый велосипедный маршрут от города Яхрома до города Дубна
        вдоль канала имени Москвы — и так рекордно длинный для России. Но это —
        только первая очередь запланированного 1000-километрового маршрута
        ВЕЛО1, который должен связать Москву и Санкт-Петербург.
      </>
    ),
    address: 'ВЕЛО1: Яхрома — Дубна',
    href: 'https://2gis.onelink.me/nw2S/5bzuo0re',
    map: {
      center: [37.503309, 56.348073],
      zoom: 12.18,
    },
    polylineWithoutMarksSE: true,
    polyline: {
      color: '#F95B49',
      coordinates: [
        [37.487064, 56.290888],
        [37.487582, 56.290976],
        [37.48769, 56.290995],
        [37.487868, 56.291025],
        [37.487976, 56.291043],
        [37.488121, 56.291068],
        [37.488093, 56.291106],
        [37.488135, 56.291722],
        [37.488258, 56.291945],
        [37.488774, 56.292475],
        [37.488809, 56.292534],
        [37.488823, 56.292883],
        [37.488807, 56.29294],
        [37.488809, 56.292984],
        [37.488949, 56.293235],
        [37.489149, 56.29334],
        [37.48956, 56.29354],
        [37.489614, 56.293551],
        [37.489753, 56.293566],
        [37.489813, 56.293579],
        [37.489975, 56.293637],
        [37.490091, 56.293691],
        [37.490197, 56.293765],
        [37.490245, 56.293785],
        [37.490504, 56.293857],
        [37.490622, 56.293889],
        [37.490673, 56.293891],
        [37.49072, 56.293883],
        [37.490766, 56.293865],
        [37.491125, 56.293649],
        [37.491179, 56.293611],
        [37.491204, 56.293563],
        [37.491285, 56.293287],
        [37.491302, 56.293255],
        [37.491449, 56.293136],
        [37.491484, 56.293103],
        [37.491604, 56.292901],
        [37.491646, 56.292867],
        [37.491701, 56.292846],
        [37.491782, 56.29284],
        [37.491857, 56.29285],
        [37.491899, 56.292875],
        [37.491918, 56.292921],
        [37.491908, 56.292974],
        [37.491885, 56.293055],
        [37.490295, 56.295378],
        [37.489331, 56.296505],
        [37.489204, 56.296629],
        [37.489099, 56.296683],
        [37.489009, 56.296701],
        [37.488913, 56.296718],
        [37.488701, 56.296718],
        [37.488291, 56.296672],
        [37.488188, 56.296672],
        [37.488066, 56.296683],
        [37.487983, 56.296718],
        [37.487944, 56.296775],
        [37.487722, 56.297411],
        [37.487521, 56.298056],
        [37.48754, 56.298135],
        [37.487604, 56.298199],
        [37.48772, 56.29826],
        [37.48837, 56.298407],
        [37.488458, 56.298438],
        [37.488528, 56.298488],
        [37.488537, 56.298531],
        [37.48854, 56.29857],
        [37.488638, 56.300036],
        [37.488624, 56.300194],
        [37.48856, 56.300654],
        [37.488451, 56.301699],
        [37.488406, 56.302377],
        [37.488387, 56.302945],
        [37.488393, 56.303754],
        [37.488381, 56.304479],
        [37.488458, 56.305064],
        [37.488669, 56.306427],
        [37.488759, 56.307465],
        [37.488939, 56.308356],
        [37.489125, 56.309787],
        [37.489317, 56.311082],
        [37.489555, 56.312765],
        [37.48985, 56.315055],
        [37.490036, 56.315947],
        [37.490364, 56.317316],
        [37.490543, 56.317891],
        [37.490793, 56.318547],
        [37.491127, 56.319267],
        [37.491506, 56.319991],
        [37.492141, 56.321096],
        [37.493014, 56.322373],
        [37.494336, 56.324295],
        [37.495433, 56.325846],
        [37.496588, 56.327493],
        [37.496961, 56.328085],
        [37.497519, 56.329027],
        [37.498276, 56.330249],
        [37.498674, 56.330952],
        [37.499046, 56.3318],
        [37.499816, 56.333526],
        [37.500715, 56.335964],
        [37.501247, 56.337326],
        [37.501863, 56.33894],
        [37.502396, 56.340395],
        [37.503525, 56.343328],
        [37.504097, 56.344597],
        [37.504847, 56.346047],
        [37.5059, 56.347647],
        [37.506259, 56.348185],
        [37.506368, 56.348367],
        [37.506607, 56.348813],
        [37.507036, 56.349551],
        [37.507215, 56.349849],
        [37.507572, 56.350437],
        [37.507947, 56.351054],
        [37.509025, 56.352874],
        [37.509404, 56.353519],
        [37.509415, 56.353543],
        [37.509494, 56.353708],
        [37.509622, 56.354154],
        [37.510001, 56.355393],
        [37.510321, 56.356484],
        [37.510322, 56.356487],
        [37.510527, 56.357232],
        [37.510591, 56.35747],
        [37.510655, 56.357862],
        [37.510745, 56.358696],
        [37.510867, 56.360035],
        [37.510905, 56.360869],
        [37.510937, 56.361521],
        [37.510925, 56.362593],
        [37.510886, 56.363612],
        [37.51082, 56.364206],
        [37.510796, 56.364417],
        [37.51061, 56.365703],
        [37.510475, 56.366244],
        [37.510026, 56.367993],
        [37.509782, 56.368727],
        [37.509256, 56.370112],
        [37.508794, 56.371191],
        [37.508448, 56.371971],
        [37.508127, 56.372577],
        [37.506824, 56.374631],
        [37.506413, 56.375247],
        [37.506291, 56.375468],
        [37.506131, 56.375959],
        [37.505906, 56.376362],
        [37.505207, 56.377395],
        [37.503635, 56.379752],
        [37.502159, 56.381959],
        [37.500118, 56.384967],
        [37.498892, 56.386808],
        [37.497833, 56.388403],
        [37.497371, 56.388987],
        [37.497204, 56.389168],
        [37.496993, 56.389332],
        [37.496826, 56.389471],
        [37.49671, 56.389634],
        [37.496646, 56.38983],
        [37.496608, 56.38998],
        [37.496582, 56.390126],
        [37.496499, 56.390293],
        [37.495953, 56.391133],
        [37.49501, 56.392496],
        [37.49354, 56.394739],
        [37.49175, 56.397319],
        [37.490652, 56.39892],
        [37.490171, 56.3995],
        [37.489638, 56.400045],
        [37.488977, 56.400803],
        [37.488676, 56.40118],
        [37.488464, 56.4015],
        [37.488323, 56.401739],
        [37.488092, 56.402141],
        [37.487829, 56.402511],
        [37.487578, 56.402984],
        [37.487322, 56.403671],
        [37.487071, 56.404383],
        [37.486815, 56.404941],
        [37.486731, 56.405],
      ],
    } as PolylineOptions,
  },
  {
    name: 'Город с самым большим количеством мостов',
    tag: 'Город',
    title: 'Город с самым большим количеством мостов',
    description: (
      <>
        Пётр I был бы не в восторге. По его замыслу, петербуржцы должны были
        передвигаться по городу на лодках. Строительства мостов следовало по
        возможности избегать, чтобы не мешать судоходству. А теперь у Петербурга
        — российский рекорд: более 430 мостов. 18 мостов Петербурга разводятся —
        и это обеспечивает не только судоходство, но и популярный туристический
        аттракцион. Кстати, развести мосты можно и самому в любое время, нажав
        на них на карте в 2ГИС.
      </>
    ),
    description_mob: (
      <>
        У Петербурга — российский рекорд: более 430 мостов. 18 мостов Петербурга
        разводятся — и это обеспечивает не только судоходство, но и популярный
        туристический аттракцион.
      </>
    ),
    address: 'Санкт-Петербург',
    href: 'https://2gis.onelink.me/nw2S/her66rmh',
    map: {
      center: [30.33614904877055, 59.92983842477668],
      zoom: 9.494726875517774,
    },
    selectObjects: ['5348647327760881'],
    markerCords: [30.33614904877055, 59.92983842477668],
  },
  {
    name: 'Самый большой регион России',
    tag: 'Город',
    title: 'Самый большой регион России',
    description: (
      <>
        Республика Саха (Якутия) больше Аргентины — восьмого по площади среди
        всех государств мира. На её территории — 3 миллиона км² — поместились бы
        шесть Франций или Испаний.
        <br />
        <br />
        Это ещё и один из самых суровых и малонаселённых регионов России. В
        Республике Саха проживает всего миллион человек. То есть, на каждого
        жителя приходятся 3 км². А ещё в Якутии добывают четверть всех алмазов в
        мире.
      </>
    ),
    description_mob: (
      <>
        Республика Саха (Якутия) больше Аргентины — восьмого по площади среди
        всех государств мира. На её территории — 3 миллиона км² — поместились бы
        шесть Франций или Испаний.
      </>
    ),
    address: 'Республика Саха (Якутия)',
    href: 'https://2gis.onelink.me/nw2S/21woehkl',
    map: {
      center: [131.46396072018223, 65.57618756166579],
      zoom: 4.353999826227681,
    },
    polygons: createMapPolygons(regionBoundaries.yakutia),
    markerCords: [131.46396072018223, 65.57618756166579],
  },
  {
    name: 'Ночлег на самой большой высоте',
    tag: 'Место',
    title: 'Ночлег на самой большой высоте',
    description: (
      <>
        У «Липрус» на южном склоне Эльбруса нет звёзд и спа. Зато есть туалет,
        душ, отопление, вай-фай, шведский стол и мягкие полки, на которых
        туристы отдыхают в спальных мешках. И это — очень неплохо для высоты
        3912 метров над уровнем моря: гостиниц, отелей или хостелов выше в
        России точно нет.
        <br />
        <br />В собранном из лёгких модулей «Липрусе» одновременно могут жить 24
        человека. Летом 2026 года здесь можно заночевать за 5600 рублей с
        человека. Добираются сюда вначале канатной дорогой, потом — снегоходами.
        Здесь альпинисты проходят акклиматизацию перед восхождением на Эльбрус.
      </>
    ),
    description_mob: (
      <>
        У «Липрус» на южном склоне Эльбруса нет звёзд и спа. Зато есть туалет,
        душ, отопление, вай-фай, шведский стол и мягкие полки, на которых
        туристы отдыхают в спальных мешках. И это — очень неплохо для высоты
        3912 метров над уровнем моря.
        <br />
        <br />
      </>
    ),
    address:
      'Высокогорный приют «Космос Эльбрус Липрус Лагерь» в Кабардино-Балкарии',
    href: 'https://2gis.onelink.me/nw2S/rqbwfbsa',
    map: {
      center: [42.457105, 43.30974],
      zoom: 19.53,
    },
    selectObjects: ['70030076307346205'],
    markerCords: [42.45710669160428, 43.309762163723306],
  },
  {
    name: 'Самая длинная набережная',
    tag: 'Место',
    title: 'Самая длинная набережная',
    description: (
      <>
        Самая длинная в России (и не только среди морских) набережная Геленджика
        опоясывает Геленджикскую бухту. Длина набережной — около 14 км. То есть,
        неспешная прогулка вдоль её белой балюстрады затянется на три часа. По
        пути — рощи реликтовой пицундской сосны, кафе и памятники разной степени
        забавности. Например, на одном из вековых дубов у моря есть златая цепь
        и русалка на ветвях, а рядом — кот учёный.
        <br />
        <br />
        Что касается самой длинной речной набережной России, то она находится на
        Волге, в Саратове и вдвое уступает геленджикской: её длина — 6,4 км.
      </>
    ),
    description_mob: (
      <>
        Самая длинная в России (и не только среди морских) набережная Геленджика
        опоясывает Геленджикскую бухту. Длина набережной — около 14 км.
      </>
    ),
    address: 'Набережная Геленджика',
    href: 'https://2gis.onelink.me/nw2S/nxkjasqa',
    map: {
      center: [38.07647114409116, 44.563176779886874],
      zoom: 13.967363437758888,
    },
    markerCords: [38.07647114409116, 44.563176779886874],
  },
  {
    name: 'Самый маленький регион России',
    tag: 'Город',
    title: 'Самый маленький регион России',
    description: (
      <>
        Ингушетия — самый маленький из российских субъектов федерации, если не
        считать городов федерального значения. Площадь этой кавказской
        республики — 3,1 тыс. км². Это примерно в тысячу раз меньше самой
        большой области России — Республики Саха (Якутии). Если вы едете из
        Чечни в Северную Осетию, вы пересечёте Ингушетию менее чем за час.
        <br />
        <br />
        Столица Ингушетии Магас — ещё и самая молодая столица субъекта РФ. Он
        заложен в 1994 году, а статус города получил в 2000 году. Имя Магас
        досталось по наследству от столицы древнего кавказского государства
        Алания.
      </>
    ),
    description_mob: (
      <>
        Площадь Ингушетии — 3,1 тыс. км². Если вы едете из Чечни в Северную
        Осетию, вы пересечёте Ингушетию менее чем за час.
      </>
    ),
    address: 'Республика Ингушетия',
    href: 'https://2gis.onelink.me/nw2S/strq655g',
    map: {
      center: [44.99115, 43.189577],
      zoom: 10,
    },
    polygons: createMapPolygons(regionBoundaries.ingushetia),
    markerCords: [44.99115, 43.189577],
  },
  {
    name: 'Самый большой аэропорт',
    tag: 'Транспорт',
    title: 'Самый большой аэропорт',
    description: (
      <>
        Международный аэропорт Шереметьево — крупнейший в России. И по объёму
        пассажирских и грузовых перевозок. И по количеству взлётов и посадок:
        более 800 в сутки, то есть, — почти каждые две минуты. И по площади всех
        терминалов — 573 775 м²: Это 25 Красных площадей или 80 футбольных
        полей.
      </>
    ),
    description_mob: (
      <>
        Международный аэропорт Шереметьево — крупнейший в России. И по объёму
        пассажирских и грузовых перевозок. И по количеству взлётов и посадок:
        более 800 в сутки. И по площади всех терминалов — 573 775 м².
      </>
    ),
    address: 'Шереметьево в Москве',
    href: 'https://2gis.onelink.me/nw2S/v2a5uuyb',
    map: {
      center: [37.415099, 55.980696],
      zoom: 16.17,
    },
    selectObjects: ['4504235321183886'],
    markerCords: [37.415203440184015, 55.98103044728889],
  },
  {
    name: 'Самый большой вокзал',
    tag: 'Транспорт',
    title: 'Самый большой вокзал',
    description: (
      <>
        Казанский вокзал считается самым большим в России по площади: 12 000 м².
        Главное здание вокзала по проекту архитектора Алексея Щусева начали
        строить в 1913 году, основные работы завершили к 1926 году, а
        окончательно достроили только к 1940.
        <br />
        <br />
        Ильф и Петров писали, что в буфете-ресторане Казанского легко поместится
        весь Ярославский вокзал. Зато Ярославский вокзал, ворота Транссибирской
        магистрали сейчас лидирует по объёму перевозок.
      </>
    ),
    description_mob: (
      <>
        Казанский вокзал считается самым большим в России по площади: 12 000 м².
        Ильф и Петров писали, что в буфете-ресторане Казанского легко поместится
        весь Ярославский вокзал.
      </>
    ),
    address: 'Казанский вокзал в Москве',
    href: 'https://2gis.onelink.me/nw2S/z2g0dkdc',
    map: {
      center: [37.656899916331255, 55.77377959697382],
      zoom: 16.747229601369618,
    },
    selectObjects: ['4504235282657018'],
    markerCords: [37.656899916331255, 55.77377959697382],
  },
  {
    name: 'Самый большой зоопарк',
    tag: 'Место',
    title: 'Самый большой зоопарк',
    description: (
      <>
        Самую большую площадь среди всех зоопарков России занимает Ярославский
        зоопарк. Он открылся в 2008 году — к 1000-летию Ярославля. Зоопарк
        находится на краю города, практически в лесу, и занимает 120 га.
        Передвигаться по территории можно не только пешком, но и на
        электромобиле или экипаже с лошадьми.
        <br />
        <br />
        Согласно официальному сайту, летом 2026 года в Ярославском зоопарке
        живут 3263 животных, представляющих более 402 вида. Среди самых
        популярных обитателей зоопарка — белая львица Лавина.
        <br />
        <br />
        На хвост Ярославскому наступает Новосибирский зоопарк: 65 га, 11 000
        особей 770 видов. А вот Московский зоопарк — старейший в России, но не
        такой большой: 22 га, 15 000 животных 1243 видов. Во всех этих местах (и
        ещё 13 российских зоопарках) на картах 2ГИС недавно появились
        анимированные реалистичные животные.
      </>
    ),
    description_mob: (
      <>
        Самую большую площадь среди всех зоопарков России занимает Ярославский
        зоопарк. Он находится на краю города, практически в лесу, и занимает 120
        га. Летом 2026 года в Ярославском зоопарке живут 3263 животных,
        представляющих более 402 вида.
      </>
    ),
    address: 'Ярославский зоопарк',
    href: 'https://2gis.onelink.me/nw2S/o5cure86',
    map: {
      center: [39.89969219977079, 57.677290021511375],
      zoom: 17.771188277154856,
    },
    selectObjects: ['3941285329136190'],
    markerCords: [39.89969219977079, 57.677290021511375],
  },
  {
    name: 'Самый большой музей',
    tag: 'Здание',
    title: 'Самый большой музей',
    description: (
      <>
        Самый большой музей России — Эрмитаж. Общая площадь его помещений — 233
        345 м². Выставочные из них — 100 000 м².
        <br />
        <br />
        Речь идёт не только о Зимнем дворце, но и о других зданиях музейного
        комплекса на Дворцовой набережной: Запасном доме Зимнего дворца, Малом
        Эрмитаже, Большом (Старом) Эрмитаже, Новом Эрмитаже и Эрмитажном театре.
        А также — Главном штабе с импрессионистами, Музее Императорского
        фарфорового завода, реставрационно-хранительском центре «Старая Деревня»
        и Меншиковском дворце.
        <br />
        <br />В коллекции Эрмитажа — около трёх миллионов произведений
        искусства. Сориентироваться в музее поможет 2ГИС: просто откройте
        поэтажную карту здания и найдите нужный зал.
      </>
    ),
    description_mob: (
      <>
        Самый большой музей России — Эрмитаж. Общая площадь его помещений — 233
        345 м². Выставочные из них — 100 000 м². Речь идёт не только о Зимнем
        дворце, но и о других зданиях музейного комплекса. В коллекции Эрмитажа
        — около трёх миллионов произведений искусства.
      </>
    ),
    address: 'Эрмитаж в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/zhjw5sr7',
    map: {
      center: [30.313839597115294, 59.940446421426245],
      zoom: 16.994137833288637,
    },
    selectObjects: ['5348720342213024'],
    markerCords: [30.313839597115294, 59.940446421426245],
  },
  {
    name: 'Самый большой пляж',
    tag: 'Природа',
    title: 'Самый большой пляж',
    description: (
      <>
        Халактырский пляж в получасе езды от Петропавловска-Камчатского
        считается самым большим в России. Его протяжённость — от мыса
        Сигнального до устья реки Налычева — больше 50 км. Прогулка от начала до
        конца пляжа подразумевает поход с ночёвкой.
        <br />
        <br />
        Но больше всего пляж знаменит своим вулканическим чёрным песком. А ещё —
        тем, что здесь, в холодных волнах Тихого океана круглый год тренируются
        сёрферы. Незабываемую картину довершает вид на Корякский, Авачинский и
        Козельский вулканы.
      </>
    ),
    description_mob: (
      <>
        Халактырский пляж в получасе езды от Петропавловска-Камчатского
        считается самым большим в России. Его протяжённость — от мыса
        Сигнального до устья реки Налычева — больше 50 км.
      </>
    ),
    address: 'Халактырский пляж на Камчатке',
    href: 'https://2gis.onelink.me/nw2S/1yfhk9gz',
    map: {
      center: [158.857151, 52.997156, 0],
      zoom: 17.65,
    },
    viewportBounds: {
      southWest: [158.8555, 52.9945],
      northEast: [158.8675, 52.9998],
    },
    polylineWithoutMarksSE: true,
    polyline: {
      color: '#F95B49',
      coordinates: [
        [37.728267, 55.707331],
        [37.728422, 55.705788],
        [37.728961, 55.702125],
        [37.729132, 55.701618],
        [37.729497, 55.700977],
        [37.731063, 55.698679],
        [37.731256, 55.698232],
        [37.731299, 55.697796],
        [37.731256, 55.697397],
        [37.731042, 55.696914],
        [37.730724, 55.696421],
        [37.730369, 55.696012],
        [37.729761, 55.695547],
        [37.728863, 55.695105],
        [37.727349, 55.694556],
        [37.727349, 55.694556],
        [37.724843, 55.693647],
        [37.72366, 55.69308],
        [37.723044, 55.69265],
        [37.722675, 55.692325],
        [37.721202, 55.690619],
        [37.720715, 55.690194],
        [37.720227, 55.689899],
        [37.707324, 55.685536],
        [37.704435, 55.684497],
        [37.704435, 55.684497],
        [37.703116, 55.684022],
        [37.702244, 55.683625],
        [37.70128, 55.683089],
        [37.700498, 55.682669],
        [37.699842, 55.682194],
        [37.696817, 55.679569],
        [37.696357, 55.679213],
        [37.695915, 55.678922],
        [37.695229, 55.678589],
        [37.682387, 55.675031],
        [37.681703, 55.674846],
        [37.681703, 55.674846],
        [37.679626, 55.674284],
        [37.668257, 55.671135],
        [37.666605, 55.670506],
        [37.665081, 55.669816],
        [37.663622, 55.66903],
        [37.661197, 55.667481],
        [37.659225, 55.665634],
        [37.653126, 55.658585],
        [37.652315, 55.657868],
        [37.649482, 55.655779],
        [37.648674, 55.655214],
        [37.648674, 55.655214],
        [37.647022, 55.654058],
        [37.645606, 55.653278],
        [37.644212, 55.652664],
        [37.64237, 55.652238],
        [37.639434, 55.651943],
        [37.636613, 55.651839],
        [37.633676, 55.651879],
        [37.631722, 55.652021],
        [37.629997, 55.652246],
        [37.628229, 55.652562],
        [37.626408, 55.652911],
        [37.625451, 55.653093],
        [37.624369, 55.653247],
        [37.623215, 55.653342],
        [37.622065, 55.653388],
        [37.621065, 55.653395],
        [37.619462, 55.653388],
        [37.619462, 55.653388],
        [37.617851, 55.653377],
        [37.607282, 55.65327],
        [37.606737, 55.653284],
        [37.606096, 55.65332],
        [37.604691, 55.653439],
        [37.603914, 55.653465],
        [37.603253, 55.653458],
        [37.602547, 55.653418],
        [37.60174, 55.653346],
        [37.601117, 55.653269],
        [37.598651, 55.652918],
        [37.598651, 55.652918],
        [37.595637, 55.652489],
        [37.592962, 55.652103],
        [37.590059, 55.651695],
        [37.589544, 55.651636],
        [37.589032, 55.651596],
        [37.588479, 55.651578],
        [37.587145, 55.65161],
        [37.586128, 55.651693],
        [37.585124, 55.651889],
        [37.584096, 55.65215],
        [37.58305, 55.652511],
        [37.576054, 55.654922],
        [37.573806, 55.655697],
        [37.573806, 55.655697],
        [37.571559, 55.656471],
        [37.56259, 55.659562],
        [37.561877, 55.659753],
        [37.561104, 55.659932],
        [37.560301, 55.660061],
        [37.559579, 55.660145],
        [37.558827, 55.660178],
        [37.558009, 55.660182],
        [37.556508, 55.660075],
        [37.555712, 55.659958],
        [37.555064, 55.659825],
        [37.554489, 55.659667],
        [37.553826, 55.659433],
        [37.553193, 55.659158],
        [37.549569, 55.657551],
        [37.549295, 55.657449],
        [37.548987, 55.657355],
        [37.54868, 55.657276],
        [37.548362, 55.657204],
        [37.547919, 55.657118],
        [37.54744, 55.657043],
        [37.546935, 55.65698],
        [37.546413, 55.656934],
        [37.545938, 55.656929],
        [37.545339, 55.656956],
        [37.544839, 55.657012],
        [37.544334, 55.657158],
        [37.54138, 55.658191],
        [37.539303, 55.658894],
        [37.539303, 55.658894],
        [37.537227, 55.659597],
        [37.533999, 55.660821],
        [37.5335, 55.661033],
        [37.533076, 55.661225],
        [37.532775, 55.661414],
        [37.532524, 55.661621],
        [37.532253, 55.661857],
        [37.530248, 55.663982],
        [37.529674, 55.664573],
        [37.529084, 55.665039],
        [37.524483, 55.668542],
        [37.523353, 55.669167],
        [37.523353, 55.669167],
        [37.520078, 55.670981],
        [37.516662, 55.672768],
        [37.515999, 55.673051],
        [37.515277, 55.673318],
        [37.511471, 55.674506],
        [37.51074, 55.67481],
        [37.508824, 55.675908],
        [37.505409, 55.677678],
        [37.503846, 55.678501],
        [37.503846, 55.678501],
        [37.502284, 55.679324],
        [37.501332, 55.6796],
        [37.500084, 55.679825],
        [37.498861, 55.679979],
        [37.495825, 55.680304],
        [37.495083, 55.680398],
        [37.494703, 55.680499],
        [37.494264, 55.680651],
        [37.493789, 55.680829],
        [37.492554, 55.681444],
        [37.492027, 55.681707],
        [37.491577, 55.682024],
        [37.491165, 55.682347],
        [37.490823, 55.682649],
        [37.490582, 55.682892],
        [37.490396, 55.68312],
        [37.487764, 55.686332],
        [37.487042, 55.687015],
        [37.486344, 55.687585],
        [37.485216, 55.688284],
        [37.485216, 55.688284],
        [37.484089, 55.688983],
        [37.480298, 55.691195],
        [37.479856, 55.691395],
        [37.479385, 55.691561],
        [37.478721, 55.691678],
        [37.478073, 55.691761],
        [37.47738, 55.691811],
        [37.476614, 55.691861],
        [37.475833, 55.691894],
        [37.472532, 55.692011],
        [37.47184, 55.692086],
        [37.471177, 55.692186],
        [37.470513, 55.692344],
        [37.469924, 55.692502],
        [37.469335, 55.69276],
        [37.468892, 55.693043],
        [37.466074, 55.69619],
        [37.464766, 55.697313],
        [37.464766, 55.697313],
        [37.463459, 55.698436],
        [37.461798, 55.699947],
        [37.460268, 55.701506],
        [37.45887, 55.703122],
        [37.45684, 55.705884],
        [37.454644, 55.709379],
        [37.454609, 55.709449],
        [37.453028, 55.71218],
        [37.451999, 55.714505],
        [37.451933, 55.714659],
        [37.451933, 55.714659],
        [37.451378, 55.715946],
        [37.447441, 55.724841],
        [37.44653, 55.7272],
        [37.445905, 55.728815],
        [37.445905, 55.728815],
        [37.445594, 55.729621],
        [37.445293, 55.730447],
        [37.445061, 55.731297],
        [37.444895, 55.732067],
        [37.44477, 55.733103],
        [37.444799, 55.733593],
        [37.444903, 55.734092],
        [37.445065, 55.73455],
        [37.445345, 55.73499],
        [37.445684, 55.735389],
        [37.44612, 55.735834],
        [37.459034, 55.747522],
        [37.459763, 55.748176],
        [37.459763, 55.748176],
        [37.460492, 55.74883],
        [37.462292, 55.75056],
        [37.467732, 55.755896],
        [37.468901, 55.757129],
        [37.469799, 55.758339],
        [37.470615, 55.759614],
        [37.470839, 55.760087],
        [37.471296, 55.761054],
        [37.471296, 55.761054],
        [37.471901, 55.762331],
        [37.472813, 55.764258],
        [37.473249, 55.765298],
        [37.473733, 55.766548],
        [37.474061, 55.767514],
        [37.474419, 55.768456],
        [37.47472, 55.769248],
        [37.476466, 55.772804],
        [37.476665, 55.773201],
        [37.476881, 55.773531],
        [37.477123, 55.773828],
        [37.477409, 55.774093],
        [37.477807, 55.774379],
        [37.478404, 55.774707],
        [37.478937, 55.774949],
        [37.47967, 55.7752],
        [37.480219, 55.775366],
        [37.480821, 55.77548],
        [37.481485, 55.775563],
        [37.482259, 55.77561],
        [37.48358, 55.775667],
        [37.485561, 55.77574],
        [37.485561, 55.77574],
        [37.487543, 55.775814],
        [37.48849, 55.775827],
        [37.489729, 55.775817],
        [37.491445, 55.775815],
        [37.492755, 55.775823],
        [37.493873, 55.775849],
        [37.499775, 55.776068],
        [37.500508, 55.776078],
        [37.501117, 55.776051],
        [37.501675, 55.775975],
        [37.502694, 55.775796],
        [37.504136, 55.775572],
        [37.505374, 55.775445],
        [37.506545, 55.775413],
        [37.507871, 55.77544],
        [37.508905, 55.775479],
        [37.509961, 55.775554],
        [37.511777, 55.77574],
        [37.513013, 55.775922],
        [37.517245, 55.776469],
        [37.51935, 55.776737],
        [37.51935, 55.776737],
        [37.521455, 55.777005],
        [37.522365, 55.777161],
        [37.523132, 55.777327],
        [37.523854, 55.777502],
        [37.524458, 55.777676],
        [37.525033, 55.777851],
        [37.525563, 55.778042],
        [37.526109, 55.77829],
        [37.526639, 55.778556],
        [37.527096, 55.778822],
        [37.527538, 55.779113],
        [37.527931, 55.779404],
        [37.52826, 55.779686],
        [37.528525, 55.779977],
        [37.528849, 55.780342],
        [37.529144, 55.780741],
        [37.529365, 55.781123],
        [37.529571, 55.781571],
        [37.529733, 55.782036],
        [37.530129, 55.783936],
        [37.530246, 55.784249],
        [37.53045, 55.784556],
        [37.530691, 55.784801],
        [37.531046, 55.785092],
        [37.53194, 55.785727],
        [37.533155, 55.786589],
        [37.533155, 55.786589],
        [37.535867, 55.788513],
        [37.536733, 55.789057],
        [37.537528, 55.789468],
        [37.538346, 55.789804],
        [37.539142, 55.790065],
        [37.539982, 55.790215],
        [37.540977, 55.79029],
        [37.541861, 55.790265],
        [37.549455, 55.789775],
        [37.550313, 55.789742],
        [37.551161, 55.789737],
        [37.55194, 55.789781],
        [37.552671, 55.789858],
        [37.553353, 55.789945],
        [37.55423, 55.790121],
        [37.554659, 55.790247],
        [37.555068, 55.790401],
        [37.555526, 55.790632],
        [37.555887, 55.790873],
        [37.557136, 55.791966],
        [37.557136, 55.791966],
        [37.558005, 55.792725],
        [37.558837, 55.793418],
        [37.559461, 55.793967],
        [37.559863, 55.794226],
        [37.560341, 55.794429],
        [37.560867, 55.794594],
        [37.561481, 55.794698],
        [37.562151, 55.794747],
        [37.562813, 55.794752],
        [37.563544, 55.794731],
        [37.564226, 55.794659],
        [37.564879, 55.794544],
        [37.565503, 55.794379],
        [37.568699, 55.793155],
        [37.569255, 55.792974],
        [37.569937, 55.792804],
        [37.570648, 55.792694],
        [37.581047, 55.792079],
        [37.582002, 55.792062],
        [37.58282, 55.792073],
        [37.58357, 55.792112],
        [37.586133, 55.792294],
        [37.586133, 55.792294],
        [37.58777, 55.79241],
        [37.589384, 55.792491],
        [37.598569, 55.792846],
        [37.599262, 55.792937],
        [37.59991, 55.793045],
        [37.600472, 55.793185],
        [37.601164, 55.793409],
        [37.606772, 55.795627],
        [37.60781, 55.795962],
        [37.608755, 55.796203],
        [37.609849, 55.79636],
        [37.610946, 55.796438],
        [37.615134, 55.796519],
        [37.615134, 55.796519],
        [37.620213, 55.796613],
        [37.621157, 55.796589],
        [37.622123, 55.796493],
        [37.623003, 55.796372],
        [37.623904, 55.796155],
        [37.628431, 55.794563],
        [37.629311, 55.794333],
        [37.630448, 55.794116],
        [37.631435, 55.793984],
        [37.632701, 55.793923],
        [37.634239, 55.793885],
        [37.634239, 55.793885],
        [37.640941, 55.793718],
        [37.64195, 55.793766],
        [37.642894, 55.793851],
        [37.643945, 55.793996],
        [37.650018, 55.79519],
        [37.651241, 55.795359],
        [37.652271, 55.795407],
        [37.653408, 55.795383],
        [37.654502, 55.795286],
        [37.658279, 55.794756],
        [37.659309, 55.794623],
        [37.660403, 55.794599],
        [37.66948, 55.794563],
        [37.670553, 55.794514],
        [37.671583, 55.794406],
        [37.672613, 55.794225],
        [37.673557, 55.793959],
        [37.674479, 55.793658],
        [37.675273, 55.793344],
        [37.675917, 55.79297],
        [37.676497, 55.792584],
        [37.679132, 55.79032],
        [37.679132, 55.79032],
        [37.681818, 55.788012],
        [37.682633, 55.787542],
        [37.683535, 55.787168],
        [37.684693, 55.786757],
        [37.689307, 55.785683],
        [37.690122, 55.785454],
        [37.690873, 55.785189],
        [37.692375, 55.78467],
        [37.693191, 55.784465],
        [37.694092, 55.784272],
        [37.699327, 55.783355],
        [37.700057, 55.78315],
        [37.700572, 55.782945],
        [37.701108, 55.782619],
        [37.701559, 55.782293],
        [37.701988, 55.781907],
        [37.70231, 55.781497],
        [37.702701, 55.780744],
        [37.702923, 55.780319],
        [37.702923, 55.780319],
        [37.703403, 55.779367],
        [37.703404, 55.779366],
        [37.703514, 55.779162],
        [37.703604, 55.778945],
        [37.703675, 55.778685],
        [37.7037, 55.778436],
        [37.703694, 55.778202],
        [37.703656, 55.778037],
        [37.703569, 55.777823],
        [37.702531, 55.775537],
        [37.70253, 55.775534],
        [37.702406, 55.77513],
        [37.702405, 55.775125],
        [37.702368, 55.774763],
        [37.702368, 55.774759],
        [37.702393, 55.774397],
        [37.702393, 55.774395],
        [37.702443, 55.774096],
        [37.702444, 55.774091],
        [37.702568, 55.773764],
        [37.70257, 55.773759],
        [37.702743, 55.773453],
        [37.702745, 55.77345],
        [37.702955, 55.773151],
        [37.702956, 55.77315],
        [37.703241, 55.772775],
        [37.703244, 55.772771],
        [37.704616, 55.771339],
        [37.70512, 55.77071],
        [37.705353, 55.770317],
        [37.705549, 55.769876],
        [37.705608, 55.76965],
        [37.706656, 55.764729],
        [37.706656, 55.764729],
        [37.706702, 55.764514],
        [37.706947, 55.763364],
        [37.706948, 55.763361],
        [37.70714, 55.762818],
        [37.707141, 55.762814],
        [37.707421, 55.762274],
        [37.707426, 55.762267],
        [37.707898, 55.761711],
        [37.707906, 55.761703],
        [37.708495, 55.761263],
        [37.708499, 55.76126],
        [37.709457, 55.760645],
        [37.709464, 55.760641],
        [37.713477, 55.758641],
        [37.715215, 55.757687],
        [37.716322, 55.756921],
        [37.717107, 55.756227],
        [37.717815, 55.755476],
        [37.718364, 55.754801],
        [37.718996, 55.753775],
        [37.718996, 55.753775],
        [37.71912, 55.753575],
        [37.719657, 55.752703],
        [37.720106, 55.751879],
        [37.720106, 55.751878],
        [37.720661, 55.750937],
        [37.721263, 55.749794],
        [37.721501, 55.748815],
        [37.721439, 55.747975],
        [37.721234, 55.747205],
        [37.720722, 55.746381],
        [37.719975, 55.745604],
        [37.71675, 55.742771],
        [37.716748, 55.74277],
        [37.715598, 55.741657],
        [37.715592, 55.74165],
        [37.715077, 55.74085],
        [37.715074, 55.740844],
        [37.714759, 55.739999],
        [37.714758, 55.739991],
        [37.714716, 55.739261],
        [37.714716, 55.739257],
        [37.714798, 55.738538],
        [37.714799, 55.738532],
        [37.715045, 55.737814],
        [37.71505, 55.737806],
        [37.715543, 55.737098],
        [37.71555, 55.737091],
        [37.716311, 55.736418],
        [37.71632, 55.736412],
        [37.717452, 55.735728],
        [37.717461, 55.735723],
        [37.718757, 55.735143],
        [37.718767, 55.735139],
        [37.725074, 55.733125],
        [37.729857, 55.731737],
        [37.729857, 55.731737],
        [37.730402, 55.731578],
        [37.732163, 55.731039],
        [37.734005, 55.730479],
        [37.734553, 55.730273],
        [37.735023, 55.730056],
        [37.735419, 55.729817],
        [37.735729, 55.729505],
        [37.73601, 55.729182],
        [37.736235, 55.728862],
        [37.736449, 55.728463],
        [37.736599, 55.728101],
        [37.7366, 55.728091],
        [37.736644, 55.727785],
        [37.73662, 55.727385],
        [37.736509, 55.72701],
        [37.736304, 55.726571],
        [37.735999, 55.726047],
        [37.733896, 55.723146],
        [37.733445, 55.722494],
        [37.732973, 55.721998],
        [37.728832, 55.718663],
        [37.728467, 55.718264],
        [37.728252, 55.71795],
        [37.728124, 55.717575],
        [37.728016, 55.717188],
        [37.727909, 55.716826],
        [37.727973, 55.716378],
        [37.728252, 55.715436],
        [37.729154, 55.713296],
        [37.729325, 55.712583],
        [37.729261, 55.711942],
        [37.728308, 55.709551],
        [37.728174, 55.709055],
        [37.728144, 55.708544],
        [37.728267, 55.707331],
      ],
    } as PolylineOptions,
  },
  {
    name: 'Самый большой театр',
    tag: 'Здание',
    title: 'Самый большой театр',
    description: (
      <>
        Новосибирский театр оперы — крупнейшее театральное здание России: общая
        площадь помещений составляет 40 663 м², а объём здания — 294 340 м³.
        Махину театра венчает крупнейший в стране купол. Он считается самой
        уникальной частью здания. При диаметре 60 метров и высоте 35 метров его
        толщина — всего 8 см. По пропорциям — втрое тоньше, чем у куриного яйца.
        <br />
        <br />
        Грандиозное здание начали строить в 1931 году — тогда в Новосибирске,
        будущем миллионнике, не набиралось и 200 тысяч жителей. 12 мая 1945 года
        театр победно открылся. Рассмотреть здание в подробностях можно
        благодаря реалистичной 3D-модели в 2ГИС.
      </>
    ),
    description_mob: (
      <>
        Новосибирский театр оперы — крупнейшее театральное здание России: общая
        площадь помещений составляет 40 663 м², а объём здания — 294 340 м³.
        Махину театра венчает крупнейший в стране купол. При диаметре 60 метров
        и высоте 35 метров его толщина — всего 8 см. По пропорциям — втрое
        тоньше, чем у куриного яйца.
      </>
    ),
    address: 'НОВАТ в Новосибирске',
    href: 'https://2gis.onelink.me/nw2S/vgiqv48m',
    map: {
      center: [82.924605, 55.030489],
      zoom: 17.97,
    },
    selectObjects: ['141373143521691'],
    markerCords: [82.92470781112227, 55.03050247675601],
  },
  {
    name: 'Самый высокий флагшток России',
    tag: 'Скульптура',
    title: 'Самый высокий флагшток России',
    description: (
      <>
        Он не один! Три 179-метровых флагштока над Финским заливом — самые
        высокие в России. Самый высокий в мире флагшток находится в египетском
        Каире: 201,9 м. Также в топе — Азербайджан, Саудовская Аравия,
        Таджикистан, Северная Корея и Туркменистан. На флагштоках в парке
        300-летия развеваются российский триколор, флаг Российской империи и
        флаг СССР. Каждое полотнище весит полтонны.
        <br />
        <br />
        Композиция появилась в 2023 году, когда все трое отмечали юбилей.
        Советскому флагу исполнилось 100 лет, имперскому — 165, а современному
        триколору, изначально введённому Петром I для торгового флота, — 330
        лет. На реалистичной 3D-модели флагштоков в 2ГИС можно посмотреть как
        развеваются сами флаги — и их отражения в воде.
      </>
    ),
    description_mob: (
      <>
        Он не один! Три 179-метровых флагштока над Финским заливом — самые
        высокие в России. На флагштоках в парке 300-летия развеваются российский
        триколор, флаг Российской империи и флаг СССР. Каждое полотнище весит
        полтонны.
      </>
    ),
    address: 'Флагшток в парке 300-летия Петербурга',
    href: 'https://2gis.onelink.me/nw2S/k4cezy8q',
    mapImage: '/images/flag.jpg',
    map: {
      center: [30.188268, 59.983322],
      zoom: 16.65,
    },
    selectObjects: ['5348660212813041'],
    markerCords: [30.188268, 59.983322],
  },
  {
    name: 'Самый длинный город',
    tag: 'Город',
    title: 'Самый длинный город',
    description: (
      <>
        У Сочи не было выбора: с одной стороны — море, с другой — горы. Город
        растянулся вдоль черноморского берега на 145 км. Австралийский Перт,
        который чаще всего называют самым длинным городом в мире (150 км) тоже
        находится на побережье. Чем длиннее приморский город — тем больше
        пляжей. В Сочи их около 130: на любой вкус и бюджет.
      </>
    ),
    description_mob: (
      <>
        У Сочи не было выбора: с одной стороны — море, с другой — горы. Город
        растянулся вдоль черноморского берега на 145 км. Чем длиннее приморский
        город — тем больше пляжей. В Сочи их около 130: на любой вкус и бюджет.
      </>
    ),
    address: 'Сочи',
    href: 'https://2gis.onelink.me/nw2S/4nrzmutl',
    map: {
      center: [39.73359070964881, 43.59486108593511],
      zoom: 12.29,
    },
    selectObjects: ['4222747420917890'],
    markerCords: [39.73359070964881, 43.59486108593511],
  },
  {
    name: 'Самый маленький город',
    tag: 'Город',
    title: 'Самый маленький город',
    description: (
      <>
        По переписи 2021 года самыми малонаселёнными городами России оказались
        якутский Верхоянск, Чекалин в Тульской области и Высоцк в Ленинградской
        области. Население каждого из этих городов — около тысячи человек —
        могло бы разместиться в паре панельных девятиэтажек.
        <br />
        <br />
        Высоцк ещё и самый маленький по площади из городов России — 1,93 км². То
        есть, он меньше Центрального парка в Нью-Йорке (3,41 км²). Уже несколько
        веков это порт: корабли Высоцк принимал и когда был шведским
        Тронгзундом, и когда был финским Уурасом.
      </>
    ),
    description_mob: (
      <>
        По переписи 2021 года самыми малонаселёнными городами России оказались
        якутский Верхоянск, Чекалин в Тульской области и Высоцк в Ленинградской
        области. Население каждого из этих городов — около тысячи человек.
        Высоцк ещё и самый маленький по площади из городов России — 1,93 км².
      </>
    ),
    address: 'Высоцк в Ленинградской области',
    href: 'https://2gis.onelink.me/nw2S/udp4dfd9',
    map: {
      center: [28.572002848387086, 60.62980950326622],
      zoom: 13.44,
    },
    selectObjects: ['70030076128588480'],
    markerCords: [28.572002848387086, 60.62980950326622],
  },
  {
    name: 'Самый маленький музей',
    tag: 'Здание',
    title: 'Самый маленький музей',
    description: (
      <>
        Статус самого маленького музея — условность. Музеем (например,
        ведомственным при каком-то учреждении) может считаться и просто пара
        витрин с документами. Но именно дозорная башня на Лисьей горе
        позиционирует себя как «самый маленький музей России».
        <br />
        <br />
        Башня начала XIX века — один из символов Нижнего Тагила. Экспозиция,
        расположившаяся на площади 10 м², посвящена истории башни. А башня
        побывала пожарной каланчой, обсерваторией, наблюдательным пунктом и для
        белых, и для красных в Гражданскую войну, точкой метеонаблюдений,
        телевизионным ретранслятором и заброшкой.
      </>
    ),
    description_mob: (
      <>
        Статус самого маленького музея — условность. Но именно дозорная башня на
        Лисьей горе позиционирует себя как «самый маленький музей России».
        Экспозиция, расположившаяся на площади 10 м², посвящена истории башни.
      </>
    ),
    address: 'Башня на Лисьей горе в Нижнем Тагиле',
    href: 'https://2gis.onelink.me/nw2S/8d3io7x3',
    map: {
      center: [59.946831, 57.899487],
      zoom: 20,
    },
    selectObjects: ['6333822631153543'],
    markerCords: [59.94681093073864, 57.89953228437779],
  },
  {
    name: 'Самый длинный автобусный маршрут',
    tag: 'Транспорт',
    title: 'Самый длинный автобусный маршрут',
    description: (
      <>
        Логично, что самый длинный городской автобусный маршрут страны будет в
        самом длинном городе страны — Сочи. Путь автобуса №77п — 82 км вдоль
        побережья, от железнодорожного вокзала Сочи до железнодорожного вокзала
        посёлка Лазоревское. По пути — леса и сёла, но все они входят в
        городскую черту Большого Сочи.
      </>
    ),
    description_mob: (
      <>
        Логично, что самый длинный городской автобусный маршрут страны будет в
        самом длинном городе страны — Сочи. Путь автобуса №77п — 82 км вдоль
        побережья, от железнодорожного вокзала Сочи до железнодорожного вокзала
        посёлка Лазоревское.
      </>
    ),
    address: '№77п в Сочи',
    href: 'https://2gis.onelink.me/nw2S/h7me5v3i',
    map: {
      center: [39.561328, 43.749822],
      zoom: 10.83,
    },
    fitPolylineToView: true,
    polylineWithoutMarksSE: true,
    polyline: {
      color: '#19AA1E',
      coordinates: LONGEST_BUS_ROUTE,
    },
  },
  {
    name: 'Самый длинный трамвайный маршрут',
    tag: 'Транспорт',
    title: 'Самый длинный трамвайный маршрут',
    description: (
      <>
        Второй Московский трамвайный диаметр T2 считается самым длинным
        городским трамвайным диаметром в мире: 33 км, 79 остановок. Запущенный
        весной 2026 года маршрут идёт от метро «Чертановская» до МЦД Новогиреево
        через 13 районов и 5 округов.
        <br />
        <br />
        По пути — Москва во всём её многообразии: от сталинских высоток до
        древних монастырей. Из окна трамвая открывается вид на Чистые пруды,
        площадь Трёх вокзалов и парад гигантов модернизма — башня Госплана,
        Центросоюз Ле Корбюзье — на проспекте Сахарова. По проспекту Сахарова,
        кстати, проходит первая в России трамвайная линия без проводов.
      </>
    ),
    description_mob: (
      <>
        Второй Московский трамвайный диаметр T2 считается самым длинным
        городским трамвайным диаметром в мире: 33 км, 79 остановок. Запущенный
        весной 2026 года маршрут идёт от метро «Чертановская» до МЦД Новогиреево
        через 13 районов и 5 округов.
      </>
    ),
    address: 'T2 в Москве',
    href: 'https://2gis.onelink.me/nw2S/ws2b42j1',
    map: {
      center: [37.711848, 55.711471],
      zoom: 11.77,
    },
    fitPolylineToView: true,
    polylineWithoutMarksSE: true,
    polyline: {
      color: '#19AA1E',
      coordinates: LONGEST_TRAM_ROUTE,
    },
  },
  {
    name: 'Самый длинный троллейбусный маршрут',
    tag: 'Транспорт',
    title: 'Самый длинный троллейбусный маршрут',
    description: (
      <>
        Самый долгий путь по городу среди всех российских троллейбусов — у
        второго в Иванове: 28 км, 29 остановок. Троллейбус №2 идёт от
        железнодорожного вокзала через весь город в дачный район на восточной
        окраине. По пути он дважды пересекает Уводь — реку, на которой стоит
        Иваново.
      </>
    ),
    description_mob: (
      <>
        Самый долгий путь по городу среди всех российских троллейбусов — у
        второго в Иванове: 28 км, 29 остановок. Троллейбус №2 идёт от
        железнодорожного вокзала через весь город в дачный район на восточной
        окраине.
      </>
    ),
    address: '№2 в Иванове',
    href: 'https://2gis.onelink.me/nw2S/bh6nrbbg',
    map: {
      center: [41.013775, 56.989118],
      zoom: 13.01,
    },
    fitPolylineToView: true,
    polylineWithoutMarksSE: true,
    polyline: {
      color: '#19AA1E',
      coordinates: LONGEST_TROLLEYBUS_ROUTE,
    },
  },
  {
    name: 'Самая большая голова',
    tag: 'Скульптура',
    title: 'Самая большая голова',
    description: (
      <>
        Огромную бронзовую голову Ленина в центре Улан-Удэ установили в 1971
        году — к юбилею Ленина (с опозданием). Вес головы — 42 тонны, высота —
        7,7 метров, а вместе с постаментом — 14 метров.
        <br />
        <br />
        Оригинальная работа скульпторов Георгия и Юрия Нероды вдохновила горожан
        на множество городских легенд. Например, что это — часть незавершённого
        исполинского монумента. Если бы это было правдой, то этот Ленин был бы
        ростом с волгоградскую Родину-мать.
      </>
    ),
    description_mob: (
      <>
        Огромную бронзовую голову Ленина в центре Улан-Удэ установили в 1971
        году — к юбилею Ленина (с опозданием). Вес головы — 42 тонны, высота —
        7,7 метров, а вместе с постаментом — 14 метров.
      </>
    ),
    address: 'Памятник Ленину в Улан-Удэ',
    href: 'https://2gis.onelink.me/nw2S/8q0x085n',
    map: {
      center: [107.585158, 51.834845],
      zoom: 19.78,
    },
    selectObjects: ['5207982853849093'],
    markerCords: [107.58525156216736, 51.8348308596582],
  },
  {
    name: 'Самая высокая статуя',
    tag: 'Скульптура',
    title: 'Самая высокая статуя',
    description: (
      <>
        Сейчас 85-метровая Родина-мать — самая высокая статуя в России. А во
        время постройки (1959–1967) работа скульптора Евгения Вучетича и
        инженера Николая Никитина была самой высокой в мире статуей без учёта
        постамента. Сейчас она находится на 10 месте, уступая 182-метровому
        изваянию политического деятеля Валлаббхаи Пателя в Индии и буддам и
        божествам в Китае, Японии, Мьянме и Таиланде.
      </>
    ),
    description_mob: (
      <>
        Сейчас 85-метровая Родина-мать — самая высокая статуя в России. А во
        время постройки (1959–1967) работа скульптора Евгения Вучетича и
        инженера Николая Никитина была самой высокой в мире статуей без учёта
        постамента.
      </>
    ),
    address: '«Родина-мать зовёт!» в Волгограде',
    href: 'https://2gis.onelink.me/nw2S/9tm173wf',
    map: {
      center: [44.537191, 48.742349],
      zoom: 19.07,
    },
    selectObjects: ['4645032900427997'],
    markerCords: [44.53716749896081, 48.742337355550994],
  },
  {
    name: 'Самый высокий памятник',
    tag: 'Скульптура',
    title: 'Самый высокий памятник',
    description: (
      <>
        Обелиск Победы, возведённый в 1994–1995 годах на Поклонной горе по
        проекту скульптора Зураба Церетели, — самый высокий памятник в России.
        Высота трёхгранного штыка-обелиска — 141,8 метра. Это символическая
        цифра: каждые 10 см — один день войны. На высоте 104 метров монумент
        дополнен 25-тонной бронзовой скульптурной группой — богиней Никой в
        окружении пары ангелов. А внизу — святой Георгий на коне, поражающий
        змея.
      </>
    ),
    description_mob: (
      <>
        Обелиск Победы, возведённый в 1994–1995 годах на Поклонной горе по
        проекту скульптора Зураба Церетели, — самый высокий памятник в России.
        Высота трёхгранного штыка-обелиска — 141,8 метра. Это символическая
        цифра: каждые 10 см — один день войны.
      </>
    ),
    address: 'Обелиск Победы в Москве',
    href: 'https://2gis.onelink.me/nw2S/6uowuinx',
    map: {
      center: [37.507185, 55.731601],
      zoom: 17.4,
    },
    selectObjects: ['4504235290730985'],
    markerCords: [37.506993322884945, 55.73170957130975],
  },
  {
    name: 'Самый маленький памятник',
    tag: 'Скульптура',
    title: 'Самый маленький памятник',
    description: (
      <>
        Необычные памятники — одна из фишек Томска наряду со сказочным
        деревянным зодчеством и обилием студентов. Один из томских памятников
        претендует на звание самого маленького в мире — так его и задумал
        скульптор Олег Кислицкий. Крошечный памятник установлен в 2013 году
        возле гостиницы «Томь» и изображает героиню сказки Всеволода Гаршина.
        Высота бронзовой лягушки — 44 миллиметра.
      </>
    ),
    description_mob: (
      <>
        Крошечный памятник установлен в 2013 году возле гостиницы «Томь» и
        изображает героиню сказки Всеволода Гаршина. Высота бронзовой лягушки —
        44 миллиметра. Он претендует на звание самого маленького в мире — так
        его и задумал скульптор Олег Кислицкий.
      </>
    ),
    address: '«Лягушка-путешественница» в Томске',
    href: 'https://2gis.onelink.me/nw2S/o80sz3lp',
    map: {
      center: [84.98874144832749, 56.46084202872734],
      zoom: 17.929499931705713,
    },
    selectObjects: ['422908249768140'],
    markerCords: [84.98874144832749, 56.46084202872734],
  },
  {
    name: 'Самое высокое колесо обозрения',
    tag: 'Место',
    title: 'Самое высокое колесо обозрения',
    description: (
      <>
        В 2022 году на ВДНХ открылось самое большое в Европе колесо обозрения.
        140-метровое «Солнце Москвы» на 5 метров выше знаменитого «Глаза
        Лондона». Колесо делает круг за 19 минут. В ясную погоду Москву видно на
        50 километров вокруг.
      </>
    ),
    description_mob: (
      <>
        В 2022 году на ВДНХ открылось самое большое в Европе колесо обозрения.
        140-метровое «Солнце Москвы» на 5 метров выше знаменитого «Глаза
        Лондона». Колесо делает круг за 19 минут. В ясную погоду Москву видно на
        50 километров вокруг.
      </>
    ),
    address: '«Солнце Москвы» в Москве',
    href: 'https://2gis.onelink.me/nw2S/fjtiipjj',
    mapImage: '/images/koleso.jpg',
    map: {
      center: [37.62633, 55.826557],
      zoom: 17.64,
    },
    selectObjects: ['70030076677546623'],
    markerCords: [37.62660869217571, 55.82667974518884],
  },
  {
    name: 'Самая глубокая скважина',
    tag: 'Природа',
    title: 'Самая глубокая скважина',
    description: (
      <>
        Кольская сверхглубокая скважина возле города Заполярного остаётся самым
        глубоким проникновением в земную кору: 12, 262 км. Как от МГУ до
        Сокольников.
        <br />
        <br />
        Обычно сверхглубокие (больше 7 км) скважины появляются для добычи нефти
        или газа. Кольская же — в научных целях. Кольский полуостров выбрали за
        то, что слой молодых осадочных пород там тонкий: проще добраться до
        древних слоёв.
        <br />
        <br />
        Скважину бурили с 1970 года до начала 1990-х — и экономического краха
        СССР. Проект успел сделать вклад в знания об устройстве Земли — и
        породить городскую легенду о том, что учёные услышали из скважины звуки
        ада. Легенда породила пару фильмов.
        <br />
        <br />
        Сейчас скважина законсервирована. Снаружи она выглядит как люк чуть
        меньше метра в диаметре, закрытый металлической крышкой.
      </>
    ),
    description_mob: (
      <>
        Кольская сверхглубокая скважина возле города Заполярного остаётся самым
        глубоким проникновением в земную кору: 12,262 км. Как от МГУ до
        Сокольников. Обычно сверхглубокие (больше 7 км) скважины появляются для
        добычи нефти или газа. Кольская же — в научных целях.
      </>
    ),
    address:
      'Кольская экспериментальная опорная сверхглубокая скважина в Мурманской области',
    href: 'https://2gis.onelink.me/nw2S/u09xm9xl',
    map: {
      center: [30.61071571081534, 69.39669959476649],
      zoom: 16,
    },
    selectObjects: [
      '70030076318442442',
      '70030076318444080',
      '70030076318436912',
      '70030076318436991',
    ],
    markerCords: [30.61071571081534, 69.39669959476649],
  },
  {
    name: 'Самый холодный населённый пункт',
    tag: 'Город',
    title: 'Самый холодный населённый пункт',
    description: (
      <>
        За звание самого холодного населённого пункта в России (и на Земле)
        конкурируют якутское село Оймякон и якутский же город Верхоянск.
        <br />
        <br />
        В Верхоянске официально задокументирован метеостанцией абсолютный
        минимум для обитаемой местности — -67,8 °C. Верхоянск — в Книге рекордов
        Гиннесса с самым экстремальным диапазоном температур: 105,6 °C.
        Верхоянск — ещё и самый северный и самый малонаселённый город России
        (745 человек).
        <br />
        <br />
        Официальный минимум Оймякона — -67,7 °C. Но в 1926 году во время
        экспедиции геолог Сергей Обручев зафиксировал в Оймяконе −71,2 °C. Этот
        не совсем подтверждённый рекорд и прославил Оймякон. К тому же, Оймякон
        доступнее для желающих посетить самое холодное место на Земле. А в
        Верхоянск 8 месяцев в году можно попасть только по воздуху.
      </>
    ),
    description_mob: (
      <>
        В Верхоянске официально задокументирован метеостанцией абсолютный
        минимум для обитаемой местности — -67,8 °C. Он же — в Книге рекордов
        Гиннесса с самым экстремальным диапазоном температур: 105,6 °C.
        Верхоянск — ещё и самый северный и самый малонаселённый город России
        (745 человек).
      </>
    ),
    address: 'Верхоянск в республике Саха (Якутия)',
    href: 'https://2gis.onelink.me/nw2S/cdc2z7h9',
    map: {
      center: [133.39649264394794, 67.54998376427656],
      zoom: 13.63,
    },
    markerCords: [133.39649264394794, 67.54998376427656],
  },
  {
    name: 'Самое глубокое озеро',
    tag: 'Природа',
    title: 'Самое глубокое озеро',
    description: (
      <>
        Байкал — самое глубокое озеро не только России, но и планеты. Средняя
        глубина — 744,4 метра, максимальная — 1 642 метров. То есть, Байкал мог
        бы скрыть пять Эйфелевых башен, поставленных одна на другую.
        <br />
        <br />
        Байкал — самое большое по площади пресноводное озеро Евразии: 31 722
        км². Всё это делает Байкал крупнейшим в мире природным резервуаром
        пресной воды.
        <br />
        <br />
        Туристов же сюда привлекает живописность пейзажей, холодная и кристально
        чистая вода. Весной может быть видно на 40 метров вглубь. А зимой —
        космическая красота байкальского льда.
      </>
    ),
    description_mob: (
      <>
        Байкал — самое глубокое озеро не только России, но и планеты. Средняя
        глубина — 744,4 метра, максимальная — 1 642 метров. А ещё — самое
        большое по площади пресноводное озеро Евразии: 31 722 км².
      </>
    ),
    address: 'Байкал в Восточной Сибири',
    href: 'https://2gis.onelink.me/nw2S/m6lly1wf',
    map: {
      center: [107.95888552870258, 53.37308900698089],
      zoom: 7.271900704014342,
    },
    selectObjects: ['70030076174390618'],
    markerCords: [107.95888552870258, 53.37308900698089],
  },
  {
    name: 'Самая высокая точка России ',
    tag: 'Природа',
    title: 'Самая высокая точка России ',
    description: (
      <>
        Самая высокая из горных вершин России — кавказский Эльбрус: 5642 м над
        уровнем моря. По мировым меркам это немного: Эльбрус не попадает в
        первую сотню вершин. Она состоит в основном из семи- и восьмитысячников
        Гималаев и Каракорума.
        <br />
        <br />
        Но Эльбрус входит в список «Семи вершин» — пиков всех семи частей света.
        Из-за спорности вопроса, где проходит грань между Европой и Азией, своё
        место в этом списке Эльбрус делит с альпийским Монбланом. «Семь вершин»
        — важный список для альпинистов. Каждый год на Эльбрус поднимаются 10–15
        тысяч человек.
      </>
    ),
    description_mob: (
      <>
        Самая высокая из горных вершин России — кавказский Эльбрус: 5642 м над
        уровнем моря. Он входит в список «Семи вершин» — пиков всех семи частей
        света. Каждый год на Эльбрус поднимаются 10–15 тысяч человек.
      </>
    ),
    address: 'Эльбрус на Кавказе',
    href: 'https://2gis.onelink.me/nw2S/8prrtdpz',
    map: {
      center: [42.462496, 43.355327],
      zoom: 12.73,
    },
    selectObjects: ['70030076329642823'],
    markerCords: [42.462496, 43.355327],
  },
  {
    name: 'Самый высокий вулкан',
    tag: 'Природа',
    title: 'Самый высокий вулкан',
    description: (
      <>
        Строго говоря, Эльбрус, самая высокая гора России вообще, — тоже вулкан.
        Но спящий. А Ключевская сопка на Камчатке — активный. И демонстрирует
        это каждые пару лет, извергая лаву и пепел. Это самый высокий из
        действующих вулканов Евразии: 4754 метров над уровнем моря. Восхождения
        на Ключевскую сопку требуют опыта и отличной физической подготовки.
      </>
    ),
    description_mob: (
      <>
        Ключевская сопка на Камчатке — самый высокий из действующих вулканов
        Евразии: 4754 метров над уровнем моря. Восхождения на Ключевскую сопку
        требуют опыта и отличной физической подготовки.
      </>
    ),
    address: 'Ключевская сопка на Камчатке',
    href: 'https://2gis.onelink.me/nw2S/f2w3oq4n',
    map: {
      center: [160.6981359686212, 56.035273789698486],
      zoom: 8.99818716299533,
    },
    markerCords: [160.6981359686212, 56.035273789698486],
  },
  {
    name: 'Самый большой рукотворный парк',
    tag: 'Природа',
    title: 'Самый большой рукотворный парк',
    description: (
      <>
        В кавказском Кисловодске находится самый большой рукотворный парк в
        России: 965,8 га. Парк заложен в 1823 году и до 2016 года назывался
        Курортным. В 2016 году парк получил и статус национального. И в 2024
        году стал самой посещаемой особо охраняемой природной территорией
        России: более 5 млн гостей за год. По огромной территории парка можно не
        только гулять, но и перемещаться c помощью электрокара или канатной
        дороги.
      </>
    ),
    description_mob: (
      <>
        В кавказском Кисловодске находится самый большой рукотворный парк в
        России: 965,8 га. Парк заложен в 1823 году и до 2016 года назывался
        Курортным.
      </>
    ),
    address: 'Национальный парк Кисловодский',
    href: 'https://2gis.onelink.me/nw2S/9mm776if',
    map: {
      center: [42.751997840240065, 43.89362042319287],
      zoom: 15.374429402317066,
    },
    markerCords: [42.751997840240065, 43.89362042319287],
  },
  {
    name: 'Самый большой заповедник',
    tag: 'Природа',
    title: 'Самый большой заповедник',
    description: (
      <>
        На севере Красноярского края, на полуострове Таймыр находится самый
        большой по площади заповедник не только России, но и Евразии: 4 169 222
        га. То есть, по площади заповедник сопоставим со Швейцарией или
        Нидерландами. Территория заповедника — не только арктическая тундра на
        материке, но и множество островов и прилегающая акватория Карского моря
        и моря Лаптевых.
        <br />
        <br />
        Заповедник призван изучать и сохранять уникальную и хрупкую — особенно в
        условиях потепления климата — арктическую экосистему. Здесь гнездятся
        редкие птицы, обитают белые медведи, дикие северные олени и овцебыки.
      </>
    ),
    description_mob: (
      <>
        На севере Красноярского края, на полуострове Таймыр находится самый
        большой по площади заповедник не только России, но и Евразии: 4 169 222
        га. То есть, по площади заповедник сопоставим со Швейцарией или
        Нидерландами.
      </>
    ),
    address: 'Большой Арктический государственный природный заповедник',
    href: 'https://2gis.onelink.me/nw2S/o82wtfk6',
    map: {
      center: [90.1, 75.6],
      zoom: 4.4,
    },
    markerCords: [97.17733, 75.681799],
  },
  {
    name: 'Самый запад России',
    tag: 'Город',
    title: 'Самый запад России',
    description: (
      <>
        Самая западная точка России расположена на Балтийской косе в
        Калининградской области, недалеко от погранзаставы Нормельн. Координаты
        — 54°27'30'' с. ш. и 19°38'20'' в. д.
        <br />
        <br />
        Ближайший населенный пункт — и самый западный город России — Балтийск,
        главная база ВМФ на Балтике. На набережной Балтийска корабли встречает
        самый западный памятник России — императрице Елизавете Петровне. Он
        напоминает, что именно при Елизавете Кёнигсберг, будущий Калининград
        несколько лет (1758—1762) был в Российской империи. Парадный конный
        памятник женщине — редкость. Впрочем, в седле любительница охоты
        Елизавета держалась лучше некоторых увековеченных так мужчин.
        <br />
        <br />С Балтийской косой город связывает паром. Несколько раз в день он
        отвозит туристов к заброшенным военным аэродромам, диким пляжам и
        постапокалиптическому Ржавому городу на косе. Подробности — в гиде 2ГИС
        по Балтийску и Балтийской косе.
      </>
    ),
    description_mob: (
      <>
        Самая западная точка России расположена на Балтийской косе в
        Калининградской области, недалеко от погранзаставы Нормельн. Координаты
        — 54°27'30'' с. ш. и 19°38'20'' в. д. Ближайший населенный пункт — и
        самый западный город России — Балтийск, главная база ВМФ на Балтике.
      </>
    ),
    address: 'Балтийск в Калининградской области',
    href: 'https://2gis.onelink.me/nw2S/s2b1457e',
    map: {
      center: [19.904049078030365, 54.653111726765616],
      zoom: 11.880000274218752,
    },
    selectObjects: ['5630122304471123'],
    markerCords: [19.904049078030365, 54.653111726765616],
  },
  {
    name: 'Самый север России',
    tag: 'Город',
    title: 'Самый север России',
    description: (
      <>
        Самая северная точка России находится в окрестностях мыса Флигели на
        Земле Франца-Иосифа. Её точные координаты — 81°50′35″ с. ш. 59°14′22″ в.
        д. А самый северный населённый пункт России — город Певек. Певек начался
        в 1930-х как геологический посёлок. Считается, что Певек — прототип
        места действия популярного в советское время романа Олега Куваева
        «Территория».
        <br />
        <br />
        Сейчас Певек — важный порт на Северном Морском пути. Здесь живёт около 4
        тысяч человек. Светом и теплом их обеспечивает уникальная плавучая
        атомная теплоэлектростанция «Академик Ломоносов».
      </>
    ),
    description_mob: (
      <>
        Самая северная точка России находится в окрестностях мыса Флигели на
        Земле Франца-Иосифа. Её точные координаты — 81°50′35″ с. ш. 59°14′22″ в.
        д. А самый северный населённый пункт России — город Певек.
      </>
    ),
    address: 'Певек в Чукотском автономном округе',
    href: 'https://2gis.onelink.me/nw2S/b7pqsrdm',
    map: {
      center: [170.38579022214844, 69.68727764107338],
      zoom: 10.31,
    },
    selectObjects: ['70030076128007421'],
    markerCords: [170.38579022214844, 69.68727764107338],
  },
  {
    name: 'Самый восток России',
    tag: 'Город',
    title: 'Самый восток России',
    description: (
      <>
        Самая восточная точка России находится на острове Ратманова в Чукотском
        автономном округе. Её точные координаты — 65°48′07″ с. ш. 168°59′49″ з.
        д. Самый восточный город РФ — Анадырь. Именно в аэропорту Анадыря —
        часто закрытом из-за суровости климата — начинаются все путешествия по
        Чукотке.
        <br />
        <br />
        Рекорды Анадыря на этом не кончаются: здесь возле самого большого из
        выстроенных на вечной мерзлоте деревянных православных храмов мира стоит
        самый большой в мире Николай Чудотворец.
        <br />
        <br />
        Среди того, что Анадырь ещё может предложить туристу — резвящиеся в паре
        десятков метров от берега белухи, собственный кинофестиваль и бургеры с
        олениной и неркой. Подробности — в нашем гиде по Анадырю.
      </>
    ),
    description_mob: (
      <>
        Самая восточная точка России находится на острове Ратманова в Чукотском
        автономном округе. Её точные координаты — 65°48′07″ с. ш. 168°59′49″ з.
        д. Самый восточный город РФ — Анадырь.
      </>
    ),
    address: 'Анадырь в Чукотском автономном округе',
    href: 'https://2gis.onelink.me/nw2S/au0ch7sy',
    map: {
      center: [177.51717059659708, 64.73456026110665],
      zoom: 12,
    },
    selectObjects: ['21814933499733932'],
    markerCords: [177.51717059659708, 64.73456026110665],
  },
  {
    name: 'Самый юг России',
    tag: 'Город',
    title: 'Самый юг России',
    description: (
      <>
        Самая южная точка России находится в Дагестане в 2,2 км к востоку от
        горы Рагдан. Точные координаты — 41°11′07″ с. ш. 47°46′55″ в. д. А самый
        южный населённый пункт страны — Дербент. Говорим «самый южный город» — и
        сразу представляем набережную, пляжи, фонтаны и шашлыки. Всё это в
        Дербенте есть. Например, именно здесь расположен самый большой
        мультимедийный фонтан в России со струями до 30 метров.
        <br />
        <br />
        Но Дербент — ещё и претендент на звание старейшего города РФ. Поэтому
        здесь можно бродить по мало изменившимся за последние несколько сотен
        лет лабиринтам старого города в поисках древнейшей в России мечети.
      </>
    ),
    description_mob: (
      <>
        Самая южная точка России находится в Дагестане в 2,2 км к востоку от
        горы Рагдан. Точные координаты — 41°11′07″ с. ш. 47°46′55″ в. д. А самый
        южный населённый пункт страны — Дербент.
      </>
    ),
    address: 'Дербент в Дагестане',
    href: 'https://2gis.onelink.me/nw2S/anfv7eyd',
    map: {
      center: [48.294306428997025, 42.06164071883624],
      zoom: 13.231697736412029,
    },
    selectObjects: ['17311333880736890'],
    markerCords: [48.294306428997025, 42.06164071883624],
  },
  {
    name: 'Самый старый город России',
    tag: 'Город',
    title: 'Самый старый город России',
    description: (
      <>
        История Дербента началась в 438 году н.э — с построенной персами
        крепости, которая закрыла узкий проход между горами и морем. В
        Средневековье Дербент — главная опора Арабского халифата на Кавказе. С
        1813 года Дербент — в России.
        <br />
        <br />
        Та самая крепость сохранилась. Теперь это часть мирового наследия
        ЮНЕСКО. Также среди достопримечательностей Дербента — древнейшая мечеть
        России, построенная в 734 году, и одно из старейших кладбищ страны.
        Подробности — в гиде 2ГИС по Дербенту.
      </>
    ),
    description_mob: (
      <>
        История Дербента началась в 438 году н.э — с построенной персами
        крепости, которая закрыла узкий проход между горами и морем. Теперь это
        часть мирового наследия ЮНЕСКО.
      </>
    ),
    address: 'Дербент в Дагестане',
    href: 'https://2gis.onelink.me/nw2S/anfv7eyd',
    map: {
      center: [48.274126, 42.052933],
      zoom: 17.59,
    },
    selectObjects: ['17311333880736890'],
    markerCords: [48.294306428997025, 42.06164071883624],
  },
  {
    name: 'Самый молодой город России',
    tag: 'Город',
    title: 'Самый молодой город России',
    description: (
      <>
        Иннополис основан в 2012 году, а статус города получил в 2015 году.
        Городок построен с нуля в получасе езды от Казани. В ранних проектах он
        обозначался как «IT-деревня». Здесь есть особая экономическая зона, два
        технопарка, частный университет. А среди того, что можно увидеть на
        улицах Иннополиса — не только футуристическая архитектура, но и
        беспилотные такси и роботы-доставщики.
        <br />
        <br />
        Официально ударение в названии города падает на второе «о», как принято
        в русском языке. Но многие местные произносят название на англоязычный
        манер — с ударением на первую «о».
      </>
    ),
    description_mob: (
      <>
        Иннополис основан в 2012 году, а статус города получил в 2015 году.
        Городок построен с нуля в получасе езды от Казани. В ранних проектах он
        обозначался как «IT-деревня».
      </>
    ),
    address: 'Иннополис в Татарстане',
    href: 'https://2gis.onelink.me/nw2S/bzalq0xh',
    map: {
      center: [48.74633694011956, 55.75048376425421],
      zoom: 14.188610437203073,
    },
    selectObjects: ['2956110025720041'],
    markerCords: [48.74633694011956, 55.75048376425421],
  },
  {
    name: 'Самая популярная смотровая площадка',
    tag: 'Место',
    title: 'Самая популярная смотровая площадка',
    description: (
      <>
        Смотровая площадка Останкинской телебашни — не самая высокая в России:
        340 метров (открытая) и 337 метров (закрытая). Самая высокая — «Выше
        только любовь» на 92 этаже башни ОКО в Москва-Сити — 354 метра. Но
        смотровая площадка Останкинской телебашни — самая популярная среди
        российских у пользователей 2ГИС. К ней они чаще всего строят маршруты и
        открывают её карточку.
        <br />
        <br />
        Кроме вида на 70 км вокруг (в ясную погоду) эта смотровая площадка может
        похвастаться частично прозрачным полом, дающим возможность увидеть под
        своими ногами почти сто этажей пустоты.
      </>
    ),
    description_mob: (
      <>
        Смотровая площадка Останкинской телебашни — не самая высокая в России:
        340 метров (открытая) и 337 метров (закрытая). Но зато — самая
        популярная среди российских у пользователей 2ГИС. К ней они чаще всего
        строят маршруты и открывают её карточку.
      </>
    ),
    address: 'Останкинская телебашня в Москве',
    href: 'https://2gis.onelink.me/nw2S/cpudm5ji',
    map: {
      center: [37.611672, 55.81963],
      zoom: 16.77,
    },
    selectObjects: ['4504235282680117'],
    markerCords: [37.611650255905445, 55.81973258420984],
  },
  {
    name: 'Самая фотографируемая улица',
    tag: 'Место',
    title: 'Самая фотографируемая улица',
    description: (
      <>
        В карточке Невского проспекта в 2ГИС — около 1400 фото: это самая
        фотографируемая из российских улиц. Неудивительно: главная улица
        Петербурга растянулась на 4,5 км — от Адмиралтейства до
        Александро-Невской лавры. И состоит почти сплошь из архитектурных и
        исторических достопримечательностей — от Казанского собора до шавермы на
        Литейном.
      </>
    ),
    description_mob: (
      <>
        В карточке Невского проспекта в 2ГИС — около 1400 фото: это самая
        фотографируемая из российских улиц. Неудивительно: главная улица
        Петербурга состоит почти сплошь из архитектурных и исторических
        достопримечательностей.
      </>
    ),
    address: 'Невский проспект в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/o7s3vvkd',
    map: {
      center: [30.35307221761992, 59.93202014628075],
      zoom: 13.72774122221298,
    },
    selectObjects: ['5348763291877851'],
    markerCords: [30.35307221761992, 59.93202014628075],
  },
  {
    name: 'Самая многополосная дорога',
    tag: 'Транспорт',
    title: 'Самая многополосная дорога',
    description: (
      <>
        Самая многополосная дорога в России — Зубовский бульвар Садового кольца.
        В самых широких местах на нём в сумме насчитывается 14 полос. Для
        сравнения у МКАД — 10 полос (по 5 в каждом направлении).
      </>
    ),
    description_mob: (
      <>
        Самая многополосная дорога в России — Зубовский бульвар Садового кольца.
        В самых широких местах на нём в сумме насчитывается 14 полос. Для
        сравнения у МКАД — 10 полос (по 5 в каждом направлении).
      </>
    ),
    address: 'Зубовский бульвар в Москве',
    href: 'https://2gis.onelink.me/nw2S/r86w9fhu',
    map: {
      center: [37.591009, 55.736715],
      zoom: 16.82,
    },
    selectObjects: ['4504338361749239'],
    markerCords: [37.591216534377885, 55.73660613936521],
  },
  {
    name: 'Самая длинная улица',
    tag: 'Улица',
    title: 'Самая длинная улица',
    description: (
      <>
        Самая длинная улица в&nbsp;России (в&nbsp;пределах городов)&nbsp;&mdash;
        2-ая Продольная магистраль, которая растянулась на&nbsp;50&nbsp;км через
        весь Волгоград. Его, кстати, часто называют самым длинным городом
        России. Правда, для 2-й Продольной магистрали есть риск дисквалификации:
        в&nbsp;официальных документах она не&nbsp;числится как улица. После
        Великой Отечественной войны деньги выделялись в&nbsp;основном
        на&nbsp;магистрали&nbsp;&mdash; тогда и&nbsp;начала строиться 2-ая
        Продольная, но&nbsp;позже она была разделена на&nbsp;несколько улиц. Тем
        не&nbsp;менее и&nbsp;сегодня пользователи ищут
        её&nbsp;на&nbsp;картах&nbsp;&mdash; 2-ая Продольная по-прежнему важный
        городской ориентир.
        <br />
        <br />
        Ближайший конкурент 2-ой Продольной&nbsp;&mdash; Варшавское шоссе:
        в&nbsp;пределах Москвы его длина&nbsp;&mdash; 22,5&nbsp;км. Варшавское
        шоссе вообще аномальная зона: это еще и&nbsp;самая населённая улица
        столицы по&nbsp;количеству домов: 231 дом по&nbsp;нумерации, 1303
        объекта c&nbsp;корпусами и&nbsp;строениями. А&nbsp;ещё именно там стоит
        (или лежит?) самое длинное здание Москвы&nbsp;&mdash; знаменитый лежачий
        небоскрёб.
      </>
    ),
    description_mob: (
      <>
        Самая длинная улица в&nbsp;России (в&nbsp;пределах городов)&nbsp;— 2-ая
        Продольная магистраль, которая растянулась на&nbsp;50&nbsp;км через весь
        Волгоград. Его, кстати, часто называют самым длинным городом России.
        Правда, в&nbsp;официальных документах 2-ая Продольная для удобства
        разбита на&nbsp;участки.
      </>
    ),
    address: '2-ая Продольная магистраль в Волгограде',
    href: 'https://2gis.onelink.me/nw2S/eneycyfw',
    map: {
      center: [44.526061, 48.739234],
      zoom: 11.95,
    },
    selectObjects: [
      '4645075850100880',
      '4645075850101046',
      '4645075850100889',
      '4645075850100908',
      '4645075850101017',
    ],
  },
  {
    name: 'Самая населённая улица',
    tag: 'Улица',
    title: 'Самая населённая улица',
    description: (
      <>
        Самая населённая улица в&nbsp;России&nbsp;&mdash; Красноармейская
        в&nbsp;селе Александровское в&nbsp;Ставропольском крае: 1217 домов
        по&nbsp;нумерации или 1756&nbsp;объектов. Впрочем, это преимущественно
        частные дома.
        <br />
        <br />
        А&nbsp;улицы с&nbsp;самым большим количеством многоквартирных домов
        найдутся в&nbsp;столицах. Больше всего зданий в&nbsp;Петербурге имеет
        адрес &laquo;Набережная Обводного канала&raquo; (233 дома
        по&nbsp;нумерации, 833 объекта &mdash;&nbsp;с&nbsp;учетом литер).
        В&nbsp;Москве самый популярный адрес&nbsp;&mdash; &laquo;Варшавское
        шоссе&raquo;: 231 дом по&nbsp;нумерации, 1303 объекта с&nbsp;учётом
        корпусов и строений.
      </>
    ),
    description_mob: (
      <>
        Самая населённая улица в&nbsp;России&nbsp;— Красноармейская в&nbsp;селе
        Александровское в&nbsp;Ставропольском крае: 1217 домов по&nbsp;нумерации
        или 1756&nbsp;объектов. Преимущественно это частные дома. А&nbsp;улицы
        с&nbsp;самым большим количеством многоквартирных домов найдутся
        в&nbsp;столицах.
      </>
    ),
    address: 'Красноармейская в селе Александровское',
    href: 'https://2gis.onelink.me/nw2S/w42ymrk2',
    map: {
      center: [42.991301, 44.720101],
      zoom: 13.7,
      pitch: 39.61,
      rotation: 4.53,
    },
    selectObjects: ['70030076138417315'],
    markerCords: [43.010833, 44.717648],
  },
  {
    name: 'Самое высокое здание',
    tag: 'Здание',
    title: 'Самое высокое здание',
    description: (
      <>
        Самый высокий небоскрёб России и&nbsp;Европы, а&nbsp;также самый
        северный небоскрёб мира&nbsp;&mdash; &laquo;Лахта-центр&raquo;:
        87&nbsp;этажей, 462&nbsp;метра. У&nbsp;его ближайшего
        конкурента&nbsp;&mdash; МФК &laquo;Федерация&raquo;
        в&nbsp;Москва-Сити&nbsp;&mdash; больше этажей (95), но&nbsp;само здание
        ниже (373&nbsp;метра).
        <br />
        <br />
        Изначально штаб-квартира &laquo;Газпрома&raquo; должна была быть гораздо
        заметнее&nbsp;&mdash; и&nbsp;стоять напротив Смольного собора
        на&nbsp;правом берегу Невы&nbsp;&mdash; но&nbsp;недовольство местной
        (да&nbsp;и&nbsp;мировой) общественности этой идеей было слишком велико.
        Кстати, лучшие точки зрения (и&nbsp;фотосъёмки)
        на&nbsp;Лахту&nbsp;&mdash; с&nbsp;пляжа парка 300-летия Петербурга,
        с&nbsp;западной стрелки Елагина острова, с&nbsp;Канонерского острова
        и&nbsp;бывшей Морской набережной.
      </>
    ),
    description_mob: (
      <>
        Самый высокий небоскрёб России и&nbsp;Европы, а&nbsp;также самый
        северный небоскрёб мира&nbsp;— «Лахта-центр»: 87&nbsp;этажей,
        462&nbsp;метра. У&nbsp;его ближайшего конкурента&nbsp;— МФК «Федерация»
        в&nbsp;Москва-Сити&nbsp;— больше этажей (95), но&nbsp;само здание ниже
        (373&nbsp;метра).
      </>
    ),
    address: '«Лахта-центр» в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/y8nwn6yz',
    map: {
      center: [30.177192, 59.985399],
      zoom: 16.1,
      pitch: 45,
      rotation: -173.21,
    },
    selectObjects: ['5348660212839445'],
    markerCords: [30.176774, 59.986476],
  },
  {
    name: 'Больше всего организаций',
    tag: 'Здание',
    title: 'Больше всего организаций',
    description: (
      <>
        МФК &laquo;Федерация&raquo; в&nbsp;Москва-Сити может похвастаться
        общероссийским рекордом не&nbsp;только по&nbsp;количеству этажей (95),
        но&nbsp;и&nbsp;по&nbsp;количеству зарегистрированных в&nbsp;здании
        организаций - более 1100.
        <br />
        <br />
        Среди них&nbsp;&mdash; кофейни и&nbsp;коворкинги, косметологические и
        стоматологические клиники, бюро переводов и&nbsp;агентства недвижимости,
        фитнес-студии и&nbsp;IT-компании, инвестиционные брокеры
        и&nbsp;массажисты.
        <br />
        <br />
        Также среди арендаторов&nbsp;&mdash; магазин лимитированных кроссовок,
        дилер золотых монет, корпорация развития Дальнего Востока и Арктики,
        салон красоты &laquo;Масква&raquo; и&nbsp;организатор праздников в
        гангстерском стиле, готовый &laquo;даже детский день рождения превратить
        в&nbsp;мафиозную тусовку&raquo;.
        <br />
        <br />
        Количество организаций постоянно меняется, но&nbsp;актуальную цифру
        всегда можно посмотреть в&nbsp;карточке объекта в&nbsp;2ГИС
        в&nbsp;разделе &laquo;В&nbsp;здании&raquo;.
      </>
    ),
    description_mob: (
      <>
        МФК «Федерация» в&nbsp;Москва-Сити может похвастаться рекордом по
        количеству зарегистрированных в&nbsp;здании организаций - более 1100.
        Количество постоянно меняется, но&nbsp;актуальную цифру всегда можно
        посмотреть в&nbsp;карточке объекта в&nbsp;2ГИС в&nbsp;разделе
        «В&nbsp;здании».
      </>
    ),
    address: 'МФК «Федерация»',
    href: 'https://2gis.onelink.me/nw2S/qkwpaduk',
    map: {
      center: [37.537764, 55.751352],
      zoom: 16.2,
      pitch: 45,
    },
    selectObjects: ['4504235283023073'],
    markerCords: [37.537718, 55.7499],
  },
  {
    name: 'Самый старый жилой дом',
    tag: 'Здание',
    title: 'Самый старый жилой дом',
    description: (
      <>
        Вопрос про самый старый дом&nbsp;&mdash; всегда спорный. Слишком давно
        это было, слишком часто эти дома перестраивали до&nbsp;неузнаваемости.
        Так что в&nbsp;России есть несколько претендентов на&nbsp;роль
        старейшего жилого дома страны. <br />
        <br />
        Один из&nbsp;них&nbsp;&mdash; Дом горожанина в&nbsp;Выборге
        Ленинградской области, построенный в&nbsp;1583 году при шведах. Точнее,
        тогда построена нижняя часть дома из&nbsp;каменных валунов. В&nbsp;XVII
        веке надстроили остальную часть, увеличили изначально щелевидные оконные
        проёмы, добавили надстройку-башенку. Люди живут в&nbsp;нём с&nbsp;самого
        начала&nbsp;&mdash; и&nbsp;до&nbsp;сих пор. После реконструкции 1960-х
        годов это дом на&nbsp;две квартиры. И&nbsp;обе половины нуждаются
        в&nbsp;ремонте. <br />
        <br />
        Кроме этого дома в&nbsp;Выборге есть ещё три уникальных для России
        средневековых бюргерских дома-крепости: Усадьба бюргера, Дом купеческой
        гильдии Святого Духа и&nbsp;ещё один гильдейский дом.
        &laquo;Крепости&raquo; означают, что в&nbsp;случае войны дом был готов
        стать укрытием для хозяев.
      </>
    ),
    description_mob: (
      <>
        В&nbsp;России есть несколько претендентов на&nbsp;роль старейшего жилого
        дома. Один из&nbsp;них&nbsp;— Дом горожанина в&nbsp;Выборге. В&nbsp;1583
        году при шведах была построена нижняя часть дома, в&nbsp;XVII веке
        надстроили остальное. Люди живут в&nbsp;нём с&nbsp;самого начала&nbsp;—
        и&nbsp;до&nbsp;сих пор.
      </>
    ),
    address: 'Дом горожанина в Выборге',
    href: 'https://2gis.onelink.me/nw2S/f45m6dwn',
    map: {
      center: [28.733852, 60.712339],
      zoom: 17.5,
    },
    selectObjects: ['70030076190072817'],
    markerCords: [28.733852, 60.712339],
  },
  {
    name: 'Самое длинное здание',
    tag: 'Здание',
    title: 'Самое длинное здание',
    description: (
      <>
        Самый длинный дом Петербурга&nbsp;и, возможно, России&nbsp;&mdash;
        построенный в 1980-х на&nbsp;Васильевском острове жилой дом,
        растянувшийся на&nbsp;760 метров и&nbsp;38&nbsp;парадных на&nbsp;всю
        Новосмоленскую набережную. В&nbsp;отзывах к дому в&nbsp;2ГИС жильцы
        хвалят его за&nbsp;тепло, чистоту, близость к&nbsp;метро, сказочные
        закаты, а&nbsp;также восхищаются красотой монолитного петербургского
        брутализма, выражая надежду, что такие дома простоят ещё много сотен
        лет.
        <br />
        <br />
        Петербургский рекордсмен всего на&nbsp;30&nbsp;метров обогнал
        московского&nbsp;&mdash; знаменитый &laquo;лежачий небоскрёб&raquo;
        с&nbsp;вычислительным центром на&nbsp;Варшавском шоссе.
        А&nbsp;в&nbsp;России найдутся и полуторакилометровые жилые комплексы,
        но&nbsp;они по&nbsp;сути представляют собой отдельные дома
        с&nbsp;отдельными адресами.
      </>
    ),
    description_mob: (
      <>
        Самый длинный дом России построен в&nbsp;1980-х в&nbsp;Санкт-Петербурге.
        Он растянулся на&nbsp;760&nbsp;метров и&nbsp;38&nbsp;парадных
        на&nbsp;всю Новосмоленскую набережную. В&nbsp;отзывах в&nbsp;2ГИС жильцы
        хвалят его за&nbsp;тепло, чистоту, близость к&nbsp;метро, сказочные
        закаты.
      </>
    ),
    address: 'Новосмоленская набережная, 1 в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/yhu8mte2',
    map: {
      center: [30.22572667963923, 59.94983204676531],
      zoom: 16.5,
    },
    selectObjects: [
      '5348660212663340',
      '70030076731043333',
      '70030076731047537',
      '70030076731043658',
      '70030076731047484',
      '70030076731043674',
      '70030076731047427',
      '70030076731043688',
    ],
    markerCords: [30.230509, 59.949278],
  },
  {
    name: 'Самый многоподъездный дом',
    tag: 'Здание',
    title: 'Самый многоподъездный дом',
    description: (
      <>
        Самый многоподъездный дом страны находится в&nbsp;Набережных Челнах:
        62&nbsp;подъезда, 927&nbsp;квартир. Построенная в&nbsp;начале 1980-х
        панельная пятиэтажка свернулась улиткой&nbsp;&mdash; чтобы,
        по&nbsp;замыслу архитекторов, защитить двор от&nbsp;ветра. Во&nbsp;дворе
        уместились футбольное поле и&nbsp;детсад. &laquo;Пока шёл к&nbsp;другу,
        успел отучиться 11&nbsp;классов, пойти в&nbsp;армию и&nbsp;завести
        семью&raquo;,&nbsp;&mdash; пишет пользователь 2ГИС в&nbsp;комментариях
        к&nbsp;дому.
      </>
    ),
    description_mob: (
      <>
        Самый многоподъездный дом находится в&nbsp;Набережных Челнах:
        62&nbsp;подъезда. Пятиэтажка свернулась улиткой&nbsp;— чтобы защитить
        двор от&nbsp;ветра. «Пока шёл к&nbsp;другу, успел отучиться, пойти
        в&nbsp;армию и&nbsp;завести семью»,&nbsp;— пишет пользователь 2ГИС
        в&nbsp;комментариях.
      </>
    ),
    address: '51-й комплекс, 1 в Набережных Челнах',
    href: 'https://2gis.onelink.me/nw2S/mfegrubw',
    map: {
      center: [52.432555, 55.767298],
      zoom: 17.5,
    },
    selectObjects: ['4082022817469276'],
    markerCords: [52.432378, 55.767235],
  },
  {
    name: 'Самый многоквартирный дом',
    tag: 'Здание',
    title: 'Самый многоквартирный дом',
    description: (
      <>
        Ещё недавно российским рекордсменом был дом в&nbsp;Кудрово под
        Санкт-Петербургом&nbsp;&mdash; с&nbsp;3708&nbsp;квартирами.
        Но&nbsp;в&nbsp;2019 году рекорд был побит в&nbsp;том&nbsp;же
        Петербурге,&nbsp;&mdash; 17&nbsp;подъездным 5545-квартирным
        ЖК&nbsp;на&nbsp;улице Плесецкая в&nbsp;Приморском районе. Это
        практически дом-район, который можно долго не&nbsp;покидать, ведь здесь
        под одной крышей собрались несколько кафе и&nbsp;супермаркетов, пара
        салонов красоты, фитнес-студия, медицинский центр, школы английского
        и&nbsp;брейкданса, пять винных и&nbsp;три магазина разливного пива.
      </>
    ),
    description_mob: (
      <>
        Рекорд найдет в&nbsp;Санкт-Петербурге&nbsp;— 17&nbsp;подъездный
        5545-квартирный ЖК&nbsp;на&nbsp;улице Плесецкая в&nbsp;Приморском
        районе. Это практически дом-район, ведь здесь под одной крышей собрались
        кафе и&nbsp;супермаркеты, салоны красоты, фитнес-студия, медицинский
        центр.
      </>
    ),
    address: 'Плесецкая, 10 ст1 в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/57ix7j4k',
    map: {
      center: [30.215185, 60.035694],
      zoom: 16.1,
      pitch: 45,
    },
    selectObjects: ['70030076200136738', '30.215185', '60.03569399999999'],
    markerCords: [30.215185, 60.0363],
  },
  {
    name: 'Самая прямая улица',
    tag: 'Улица',
    title: 'Самая прямая улица',
    description: (
      <>
        И&nbsp;снова волгоградские просторы! Улица 40&nbsp;лет ВЛКСМ
        в&nbsp;Красноармейском районе Волгограда не&nbsp;изменяет своей
        идеальной прямоте на&nbsp;9115&nbsp;метрах.
      </>
    ),
    description_mob: (
      <>
        И&nbsp;снова волгоградские просторы! Улица 40&nbsp;лет ВЛКСМ
        в&nbsp;Красноармейском районе Волгограда не&nbsp;изменяет своей
        идеальной прямоте на&nbsp;9115&nbsp;метрах.
      </>
    ),
    address: 'Улица 40 лет ВЛКСМ в Волгограде',
    href: 'https://2gis.onelink.me/nw2S/4426gp8d',
    map: {
      center: [44.610251, 48.486184],
      zoom: 13,
    },
    selectObjects: ['4645075850100746'],
  },
  {
    name: 'Самая крутая улица',
    tag: 'Улица',
    title: 'Самая крутая улица',
    description: (
      <>
        Низшая и&nbsp;высшая точки дороги на&nbsp;Большой Ахун в&nbsp;Сочи
        различаются на&nbsp;553,9&nbsp;метров. Этот путь туристы преодолевают
        (на&nbsp;авто, но&nbsp;иногда и&nbsp;пешком) ради потрясающих видов,
        которые открываются с&nbsp;горы, увенчанной смотровой башней
        из&nbsp;белого известняка в&nbsp;романском стиле: видна панорама
        побережья <br />
        от&nbsp;поселка Лазаревское до&nbsp;Пицунды и&nbsp;Кавказских гор.
        <br /> <br />
        По&nbsp;пути есть еще и&nbsp;легендарная заброшка с&nbsp;эффектным
        адресом &laquo;Дорога на&nbsp;Большой Ахун, 999&raquo;: полуразрушенный
        ресторан сталинской эпохи с&nbsp;ротондой и&nbsp;колоннами. Заведение
        не&nbsp;работает уже больше полувека и&nbsp;обросло густым слоем легенд
        о&nbsp;проклятиях и&nbsp;призраках.
      </>
    ),
    description_mob: (
      <>
        Низшая и&nbsp;высшая точки дороги на&nbsp;Большой Ахун в&nbsp;Сочи
        различаются на&nbsp;553,9&nbsp;метров. Этот путь туристы преодолевают
        ради видов, которые открываются с&nbsp;горы, увенчанной смотровой
        башней: панорама от&nbsp;поселка Лазаревское до&nbsp;Пицунды
        и&nbsp;Кавказских гор.
      </>
    ),
    address: 'Дорога на Большой Ахун в Сочи',
    href: 'https://2gis.onelink.me/nw2S/ljq5vq8g',
    map: {
      center: [39.837674, 43.546098],
      zoom: 14.84,
    },
    selectObjects: [
      '4222863385043519',
      '39.83987525728432',
      '43.54559939919549',
    ],
    markerCords: [39.837633, 43.546304],
  },
  {
    name: 'Самое длинное имя улицы',
    tag: 'Улица',
    title: 'Самое длинное имя улицы',
    description: (
      <>
        Улица может быть рекордно длинной не&nbsp;только в&nbsp;метрах,
        но&nbsp;и&nbsp;в&nbsp;буквах. Так в&nbsp;Москве появились улица
        50&nbsp;лет Победы в&nbsp;Великой Отечественной войне (48&nbsp;букв
        вместе со&nbsp;словом &laquo;улица&raquo;) и&nbsp;Центральный проезд
        Хорошевского Серебряного Бора (44&nbsp;буквы).
        <br /> <br />
        Российский&nbsp;же рекордсмен находится в&nbsp;Видном. Это бульвар имени
        народного артиста СССР Мстислава Ростроповича. Потребовались
        53&nbsp;буквы, чтобы уж&nbsp;точно не&nbsp;перепутать увековеченного
        артиста с&nbsp;многочисленными Мстиславами Ростроповичами, выбравшими
        другие профессии.
      </>
    ),
    description_mob: (
      <>
        Улица может быть рекордно длинной не&nbsp;только в&nbsp;метрах,
        но&nbsp;и&nbsp;в буквах. В&nbsp;Видном находится бульвар имени народного
        артиста СССР Мстислава Ростроповича. Потребовались 53&nbsp;буквы, чтобы
        уж&nbsp;точно не перепутать увековеченного артиста с&nbsp;другими
        Ростроповичами.
      </>
    ),
    address:
      'Бульвар имени народного артиста СССР Мстислава Ростроповича в Видном',
    href: 'https://2gis.onelink.me/nw2S/ebdzkvg9',
    map: {
      center: [37.646145, 55.537777],
      zoom: 16,
    },
    selectObjects: ['70030076297835569'],
    markerCords: [37.64814, 55.537246],
  },
  {
    name: 'Самое короткое имя улицы',
    tag: 'Улица',
    title: 'Самое короткое имя улицы',
    description: (
      <>
        В&nbsp;России есть несколько улиц Роз (рекомендуем поклонникам группы
        &laquo;Ария&raquo; и&nbsp;необычных названий тур на&nbsp;такую улицу
        в&nbsp;рабочем посёлке Городище на&nbsp;окраине всё того&nbsp;же
        Волгограда) и&nbsp;много других улиц с&nbsp;названием из&nbsp;трёх букв.
        Есть и&nbsp;улицы с&nbsp;названиями из&nbsp;двух букв. Например,
        улица&nbsp;Яз в&nbsp;Казани (в&nbsp;переводе
        с&nbsp;татарского&nbsp;&mdash; &laquo;весна&raquo;) и&nbsp;улица&nbsp;Ик
        в&nbsp;Башкортостане (с&nbsp;тюркского&nbsp;&mdash; &laquo;текущая
        вода&raquo;).
        <br /> <br />
        Но&nbsp;приз за&nbsp;лаконичность достаётся садовому товариществу
        Громотуха в&nbsp;Пермском крае: вместо улиц тут линии, обозначенные
        буквами. Такие названия линий есть в&nbsp;и&nbsp;других СНТ,
        но&nbsp;в&nbsp;Громотухе дошли до&nbsp;буквы &laquo;С&raquo;,
        а&nbsp;чтобы точно закрепить победу, сделали и&nbsp;линию
        &laquo;Я&raquo;, пропустив остальные буквы алфавита.
        А&nbsp;в&nbsp;Новосибирске есть микрорайон, который называется одной
        буквой,&nbsp;&mdash; и&nbsp;это буква Щ.
      </>
    ),
    description_mob: (
      <>
        Приз за&nbsp;лаконичность получает СНТ Громотуха в&nbsp;Пермском крае:
        вместо улиц тут линии, обозначенные буквами. Такие есть
        и&nbsp;в&nbsp;других СНТ, но&nbsp;в&nbsp;Громотухе дошли до&nbsp;буквы
        «С», а&nbsp;чтобы закрепить победу, сделали и&nbsp;линию «Я», пропустив
        несколько букв алфавита.
      </>
    ),
    address: 'Линии в СНТ «Громотуха»',
    href: 'https://2gis.onelink.me/nw2S/csexcj3s',
    map: {
      center: [56.67250078066707, 57.89197460393279],
      zoom: 15.5,
    },
    selectObjects: [
      '70030076163639093',
      '56.678940999999995',
      '57.892858000000004',
    ],
    markerCords: [56.677926, 57.893435],
  },
  {
    name: 'Самое длинное название',
    tag: 'Место',
    title: 'Самое длинное название организации',
    description: (
      <>
        &laquo;Отдел надзорной деятельности и&nbsp;профилактической работы
        по&nbsp;Краснознаменскому, Неманскому, Полесскому, Славскому
        и&nbsp;Советскому городским округам управления надзорной деятельности
        и&nbsp;профилактической работы Главного управления МЧС России
        по&nbsp;Калининградской области&raquo;&nbsp;&mdash; российский рекорд:
        259&nbsp;букв. Хорошо, что по&nbsp;телефону они отвечают просто:
        &laquo;Алло, служба спасения!&raquo;
      </>
    ),
    description_mob: (
      <>
        «Отдел надзорной деятельности и&nbsp;профилактической работы
        по&nbsp;Краснознаменскому, Неманскому, Полесскому, Славскому
        и&nbsp;Советскому городским округам управления надзорной деятельности
        и&nbsp;профилактической работы Главного управления МЧС России
        по&nbsp;Калининградской области»&nbsp;— российский рекорд:
        259&nbsp;букв. Зато по&nbsp;телефону они отвечают быстро и&nbsp;просто:
        «Алло, служба спасения!»
      </>
    ),
    address: 'У калининградских спасателей',
    href: 'https://2gis.onelink.me/nw2S/09b536mi',
    map: {
      center: [21.900101, 55.075475],
      zoom: 16,
    },
    selectObjects: ['70030076250499098', '21.900101', '55.075475'],
    markerCords: [21.900187, 55.075432],
  },
  {
    name: 'Самое частое название улиц',
    tag: 'Улица',
    title: 'Самое частое название улиц',
    description: (
      <>
        Чаще на&nbsp;карте России встречается улица Центральная
        (22543&nbsp;раза), а&nbsp;также Молодёжная (16181), Школьная (13958),
        Лесная (13817), Садовая (12857), Новая (11836) и&nbsp;Советская (11545).
        <br /> <br />
        Да-да, улицы Ленина в&nbsp;этом рейтинге нет! А&nbsp;Центральная стала
        рекордсменом, потому что часто так называют главные улицы
        в&nbsp;небольших сёлах и&nbsp;деревнях.
      </>
    ),
    description_mob: (
      <>
        Чаще на&nbsp;карте России встречается улица Центральная
        (22543&nbsp;раза), а&nbsp;также Молодёжная (16181) и&nbsp;Школьная
        (13958). Улицы Ленина в&nbsp;этом рейтинге нет! А&nbsp;Центральная стала
        рекордсменом, потому что часто так называют главные улицы в&nbsp;сёлах
        и&nbsp;деревнях.
      </>
    ),
    address: 'Улица Центральная',
    href: '',
    map: {
      center: [30.571859778089774, 59.80908600130828],
      zoom: 15.3,
      pitch: 45,
    },
    selectObjects: ['5348763291882306'],
    markerCords: [30.579526, 59.81221],
  },
  {
    name: 'Самая пешеходная улица',
    tag: 'Улица',
    title: 'Самая пешеходная улица',
    description: (
      <>
        Самая длинная пешеходная улица России&nbsp;&mdash; казанская Баумана.
        Пешеходной она стала в&nbsp;1996&nbsp;году, а&nbsp;до&nbsp;этого
        по&nbsp;ней ходили троллейбусы, автобусы и&nbsp;трамваи,
        а&nbsp;в&nbsp;XIX веке&nbsp;&mdash; конка. Длина улицы
        Баумана&nbsp;&mdash; 1885&nbsp;метров. Здесь выступают музыканты
        и&nbsp;прочие уличные артисты, а&nbsp;туристы фотографируются
        с&nbsp;забавными памятниками коту казанскому и&nbsp;царской карете
        и&nbsp;едят кыстыбаи и&nbsp;элеши в&nbsp;легендарных кафе
        &laquo;Сказка&raquo; и&nbsp;&laquo;Дом чая&raquo;.
        <br /> <br />
        Разумеется, это место называют &laquo;казанским Арбатом&raquo;, как
        и&nbsp;прочие подобные улицы в&nbsp;российских городах. Это наименование
        стало таким привычным, что мы&nbsp;не&nbsp;задумываемся, насколько оно
        новое: туристическим променадом московский Арбат стал только
        в&nbsp;1980-х. Самому&nbsp;же названию улицы и&nbsp;района&nbsp;&mdash;
        сотни лет. По&nbsp;одной из&nbsp;версий оно произошло
        от&nbsp;занесённого торговцами арабского слова
        &laquo;рабат&raquo;&nbsp;&mdash; пригород. Кстати, длина московского
        Арбата&nbsp;&mdash; 1200&nbsp;метров.
      </>
    ),
    description_mob: (
      <>
        Самая длинная пешеходная улица России&nbsp;— казанская Баумана.
        Пешеходной она стала в&nbsp;1996&nbsp;году, а&nbsp;до&nbsp;этого
        по&nbsp;ней ходили троллейбусы, автобусы и&nbsp;трамваи,
        а&nbsp;в&nbsp;XIX веке&nbsp;— конка. Длина улицы Баумана&nbsp;—
        1885&nbsp;метров.
      </>
    ),
    address: 'Баумана в Казани',
    href: 'https://2gis.onelink.me/nw2S/bv6ndldf',
    map: {
      center: [49.11557112195587, 55.78946668901226],
      zoom: 16,
    },
    selectObjects: ['2956225989838340'],
    markerCords: [49.115743, 55.790198],
  },
  {
    name: 'Самая длинная линия',
    tag: 'Транспорт',
    title: 'Самая длинная линия метро',
    description: (
      <>
        Предсказуемый рекорд&nbsp;&mdash; у&nbsp;Москвы: 70&nbsp;км
        и&nbsp;31&nbsp;станция у&nbsp;Большой кольцевой линии. Она&nbsp;&mdash;
        ещё и&nbsp;самое больше метрокольцо в&nbsp;мире.
        <br />
        <br />
        Если не&nbsp;хотите ехать по&nbsp;кругу, то&nbsp;по&nbsp;протяженности
        побеждает радиальная Арбатско-Покровская линия: 45&nbsp;км, которые
        поезд проезжает чуть больше, чем за&nbsp;час. А&nbsp;по&nbsp;количеству
        станций среди радиальных линий&nbsp;&mdash; Сокольническая
        и&nbsp;Люблинско-Дмитровская (по&nbsp;26).
      </>
    ),
    description_mob: (
      <>
        Предсказуемый рекорд&nbsp;— у&nbsp;Москвы: 70&nbsp;км
        и&nbsp;31&nbsp;станция у&nbsp;Большой кольцевой линии. Она&nbsp;— ещё
        и&nbsp;самое больше метрокольцо в&nbsp;мире.
      </>
    ),
    address: 'Большая кольцевая в Москве',
    href: 'https://2gis.onelink.me/nw2S/u8ee2lmi',
    map: {
      center: [37.602047533985726, 55.72301263199004],
      zoom: 11,
    },
    polylineWithoutMarksSE: true,
    polyline: {
      color: '#00B7A6',
      coordinates: [
        [37.728267, 55.707331],
        [37.728422, 55.705788],
        [37.728961, 55.702125],
        [37.729132, 55.701618],
        [37.729497, 55.700977],
        [37.731063, 55.698679],
        [37.731256, 55.698232],
        [37.731299, 55.697796],
        [37.731256, 55.697397],
        [37.731042, 55.696914],
        [37.730724, 55.696421],
        [37.730369, 55.696012],
        [37.729761, 55.695547],
        [37.728863, 55.695105],
        [37.727349, 55.694556],
        [37.727349, 55.694556],
        [37.724843, 55.693647],
        [37.72366, 55.69308],
        [37.723044, 55.69265],
        [37.722675, 55.692325],
        [37.721202, 55.690619],
        [37.720715, 55.690194],
        [37.720227, 55.689899],
        [37.707324, 55.685536],
        [37.704435, 55.684497],
        [37.704435, 55.684497],
        [37.703116, 55.684022],
        [37.702244, 55.683625],
        [37.70128, 55.683089],
        [37.700498, 55.682669],
        [37.699842, 55.682194],
        [37.696817, 55.679569],
        [37.696357, 55.679213],
        [37.695915, 55.678922],
        [37.695229, 55.678589],
        [37.682387, 55.675031],
        [37.681703, 55.674846],
        [37.681703, 55.674846],
        [37.679626, 55.674284],
        [37.668257, 55.671135],
        [37.666605, 55.670506],
        [37.665081, 55.669816],
        [37.663622, 55.66903],
        [37.661197, 55.667481],
        [37.659225, 55.665634],
        [37.653126, 55.658585],
        [37.652315, 55.657868],
        [37.649482, 55.655779],
        [37.648674, 55.655214],
        [37.648674, 55.655214],
        [37.647022, 55.654058],
        [37.645606, 55.653278],
        [37.644212, 55.652664],
        [37.64237, 55.652238],
        [37.639434, 55.651943],
        [37.636613, 55.651839],
        [37.633676, 55.651879],
        [37.631722, 55.652021],
        [37.629997, 55.652246],
        [37.628229, 55.652562],
        [37.626408, 55.652911],
        [37.625451, 55.653093],
        [37.624369, 55.653247],
        [37.623215, 55.653342],
        [37.622065, 55.653388],
        [37.621065, 55.653395],
        [37.619462, 55.653388],
        [37.619462, 55.653388],
        [37.617851, 55.653377],
        [37.607282, 55.65327],
        [37.606737, 55.653284],
        [37.606096, 55.65332],
        [37.604691, 55.653439],
        [37.603914, 55.653465],
        [37.603253, 55.653458],
        [37.602547, 55.653418],
        [37.60174, 55.653346],
        [37.601117, 55.653269],
        [37.598651, 55.652918],
        [37.598651, 55.652918],
        [37.595637, 55.652489],
        [37.592962, 55.652103],
        [37.590059, 55.651695],
        [37.589544, 55.651636],
        [37.589032, 55.651596],
        [37.588479, 55.651578],
        [37.587145, 55.65161],
        [37.586128, 55.651693],
        [37.585124, 55.651889],
        [37.584096, 55.65215],
        [37.58305, 55.652511],
        [37.576054, 55.654922],
        [37.573806, 55.655697],
        [37.573806, 55.655697],
        [37.571559, 55.656471],
        [37.56259, 55.659562],
        [37.561877, 55.659753],
        [37.561104, 55.659932],
        [37.560301, 55.660061],
        [37.559579, 55.660145],
        [37.558827, 55.660178],
        [37.558009, 55.660182],
        [37.556508, 55.660075],
        [37.555712, 55.659958],
        [37.555064, 55.659825],
        [37.554489, 55.659667],
        [37.553826, 55.659433],
        [37.553193, 55.659158],
        [37.549569, 55.657551],
        [37.549295, 55.657449],
        [37.548987, 55.657355],
        [37.54868, 55.657276],
        [37.548362, 55.657204],
        [37.547919, 55.657118],
        [37.54744, 55.657043],
        [37.546935, 55.65698],
        [37.546413, 55.656934],
        [37.545938, 55.656929],
        [37.545339, 55.656956],
        [37.544839, 55.657012],
        [37.544334, 55.657158],
        [37.54138, 55.658191],
        [37.539303, 55.658894],
        [37.539303, 55.658894],
        [37.537227, 55.659597],
        [37.533999, 55.660821],
        [37.5335, 55.661033],
        [37.533076, 55.661225],
        [37.532775, 55.661414],
        [37.532524, 55.661621],
        [37.532253, 55.661857],
        [37.530248, 55.663982],
        [37.529674, 55.664573],
        [37.529084, 55.665039],
        [37.524483, 55.668542],
        [37.523353, 55.669167],
        [37.523353, 55.669167],
        [37.520078, 55.670981],
        [37.516662, 55.672768],
        [37.515999, 55.673051],
        [37.515277, 55.673318],
        [37.511471, 55.674506],
        [37.51074, 55.67481],
        [37.508824, 55.675908],
        [37.505409, 55.677678],
        [37.503846, 55.678501],
        [37.503846, 55.678501],
        [37.502284, 55.679324],
        [37.501332, 55.6796],
        [37.500084, 55.679825],
        [37.498861, 55.679979],
        [37.495825, 55.680304],
        [37.495083, 55.680398],
        [37.494703, 55.680499],
        [37.494264, 55.680651],
        [37.493789, 55.680829],
        [37.492554, 55.681444],
        [37.492027, 55.681707],
        [37.491577, 55.682024],
        [37.491165, 55.682347],
        [37.490823, 55.682649],
        [37.490582, 55.682892],
        [37.490396, 55.68312],
        [37.487764, 55.686332],
        [37.487042, 55.687015],
        [37.486344, 55.687585],
        [37.485216, 55.688284],
        [37.485216, 55.688284],
        [37.484089, 55.688983],
        [37.480298, 55.691195],
        [37.479856, 55.691395],
        [37.479385, 55.691561],
        [37.478721, 55.691678],
        [37.478073, 55.691761],
        [37.47738, 55.691811],
        [37.476614, 55.691861],
        [37.475833, 55.691894],
        [37.472532, 55.692011],
        [37.47184, 55.692086],
        [37.471177, 55.692186],
        [37.470513, 55.692344],
        [37.469924, 55.692502],
        [37.469335, 55.69276],
        [37.468892, 55.693043],
        [37.466074, 55.69619],
        [37.464766, 55.697313],
        [37.464766, 55.697313],
        [37.463459, 55.698436],
        [37.461798, 55.699947],
        [37.460268, 55.701506],
        [37.45887, 55.703122],
        [37.45684, 55.705884],
        [37.454644, 55.709379],
        [37.454609, 55.709449],
        [37.453028, 55.71218],
        [37.451999, 55.714505],
        [37.451933, 55.714659],
        [37.451933, 55.714659],
        [37.451378, 55.715946],
        [37.447441, 55.724841],
        [37.44653, 55.7272],
        [37.445905, 55.728815],
        [37.445905, 55.728815],
        [37.445594, 55.729621],
        [37.445293, 55.730447],
        [37.445061, 55.731297],
        [37.444895, 55.732067],
        [37.44477, 55.733103],
        [37.444799, 55.733593],
        [37.444903, 55.734092],
        [37.445065, 55.73455],
        [37.445345, 55.73499],
        [37.445684, 55.735389],
        [37.44612, 55.735834],
        [37.459034, 55.747522],
        [37.459763, 55.748176],
        [37.459763, 55.748176],
        [37.460492, 55.74883],
        [37.462292, 55.75056],
        [37.467732, 55.755896],
        [37.468901, 55.757129],
        [37.469799, 55.758339],
        [37.470615, 55.759614],
        [37.470839, 55.760087],
        [37.471296, 55.761054],
        [37.471296, 55.761054],
        [37.471901, 55.762331],
        [37.472813, 55.764258],
        [37.473249, 55.765298],
        [37.473733, 55.766548],
        [37.474061, 55.767514],
        [37.474419, 55.768456],
        [37.47472, 55.769248],
        [37.476466, 55.772804],
        [37.476665, 55.773201],
        [37.476881, 55.773531],
        [37.477123, 55.773828],
        [37.477409, 55.774093],
        [37.477807, 55.774379],
        [37.478404, 55.774707],
        [37.478937, 55.774949],
        [37.47967, 55.7752],
        [37.480219, 55.775366],
        [37.480821, 55.77548],
        [37.481485, 55.775563],
        [37.482259, 55.77561],
        [37.48358, 55.775667],
        [37.485561, 55.77574],
        [37.485561, 55.77574],
        [37.487543, 55.775814],
        [37.48849, 55.775827],
        [37.489729, 55.775817],
        [37.491445, 55.775815],
        [37.492755, 55.775823],
        [37.493873, 55.775849],
        [37.499775, 55.776068],
        [37.500508, 55.776078],
        [37.501117, 55.776051],
        [37.501675, 55.775975],
        [37.502694, 55.775796],
        [37.504136, 55.775572],
        [37.505374, 55.775445],
        [37.506545, 55.775413],
        [37.507871, 55.77544],
        [37.508905, 55.775479],
        [37.509961, 55.775554],
        [37.511777, 55.77574],
        [37.513013, 55.775922],
        [37.517245, 55.776469],
        [37.51935, 55.776737],
        [37.51935, 55.776737],
        [37.521455, 55.777005],
        [37.522365, 55.777161],
        [37.523132, 55.777327],
        [37.523854, 55.777502],
        [37.524458, 55.777676],
        [37.525033, 55.777851],
        [37.525563, 55.778042],
        [37.526109, 55.77829],
        [37.526639, 55.778556],
        [37.527096, 55.778822],
        [37.527538, 55.779113],
        [37.527931, 55.779404],
        [37.52826, 55.779686],
        [37.528525, 55.779977],
        [37.528849, 55.780342],
        [37.529144, 55.780741],
        [37.529365, 55.781123],
        [37.529571, 55.781571],
        [37.529733, 55.782036],
        [37.530129, 55.783936],
        [37.530246, 55.784249],
        [37.53045, 55.784556],
        [37.530691, 55.784801],
        [37.531046, 55.785092],
        [37.53194, 55.785727],
        [37.533155, 55.786589],
        [37.533155, 55.786589],
        [37.535867, 55.788513],
        [37.536733, 55.789057],
        [37.537528, 55.789468],
        [37.538346, 55.789804],
        [37.539142, 55.790065],
        [37.539982, 55.790215],
        [37.540977, 55.79029],
        [37.541861, 55.790265],
        [37.549455, 55.789775],
        [37.550313, 55.789742],
        [37.551161, 55.789737],
        [37.55194, 55.789781],
        [37.552671, 55.789858],
        [37.553353, 55.789945],
        [37.55423, 55.790121],
        [37.554659, 55.790247],
        [37.555068, 55.790401],
        [37.555526, 55.790632],
        [37.555887, 55.790873],
        [37.557136, 55.791966],
        [37.557136, 55.791966],
        [37.558005, 55.792725],
        [37.558837, 55.793418],
        [37.559461, 55.793967],
        [37.559863, 55.794226],
        [37.560341, 55.794429],
        [37.560867, 55.794594],
        [37.561481, 55.794698],
        [37.562151, 55.794747],
        [37.562813, 55.794752],
        [37.563544, 55.794731],
        [37.564226, 55.794659],
        [37.564879, 55.794544],
        [37.565503, 55.794379],
        [37.568699, 55.793155],
        [37.569255, 55.792974],
        [37.569937, 55.792804],
        [37.570648, 55.792694],
        [37.581047, 55.792079],
        [37.582002, 55.792062],
        [37.58282, 55.792073],
        [37.58357, 55.792112],
        [37.586133, 55.792294],
        [37.586133, 55.792294],
        [37.58777, 55.79241],
        [37.589384, 55.792491],
        [37.598569, 55.792846],
        [37.599262, 55.792937],
        [37.59991, 55.793045],
        [37.600472, 55.793185],
        [37.601164, 55.793409],
        [37.606772, 55.795627],
        [37.60781, 55.795962],
        [37.608755, 55.796203],
        [37.609849, 55.79636],
        [37.610946, 55.796438],
        [37.615134, 55.796519],
        [37.615134, 55.796519],
        [37.620213, 55.796613],
        [37.621157, 55.796589],
        [37.622123, 55.796493],
        [37.623003, 55.796372],
        [37.623904, 55.796155],
        [37.628431, 55.794563],
        [37.629311, 55.794333],
        [37.630448, 55.794116],
        [37.631435, 55.793984],
        [37.632701, 55.793923],
        [37.634239, 55.793885],
        [37.634239, 55.793885],
        [37.640941, 55.793718],
        [37.64195, 55.793766],
        [37.642894, 55.793851],
        [37.643945, 55.793996],
        [37.650018, 55.79519],
        [37.651241, 55.795359],
        [37.652271, 55.795407],
        [37.653408, 55.795383],
        [37.654502, 55.795286],
        [37.658279, 55.794756],
        [37.659309, 55.794623],
        [37.660403, 55.794599],
        [37.66948, 55.794563],
        [37.670553, 55.794514],
        [37.671583, 55.794406],
        [37.672613, 55.794225],
        [37.673557, 55.793959],
        [37.674479, 55.793658],
        [37.675273, 55.793344],
        [37.675917, 55.79297],
        [37.676497, 55.792584],
        [37.679132, 55.79032],
        [37.679132, 55.79032],
        [37.681818, 55.788012],
        [37.682633, 55.787542],
        [37.683535, 55.787168],
        [37.684693, 55.786757],
        [37.689307, 55.785683],
        [37.690122, 55.785454],
        [37.690873, 55.785189],
        [37.692375, 55.78467],
        [37.693191, 55.784465],
        [37.694092, 55.784272],
        [37.699327, 55.783355],
        [37.700057, 55.78315],
        [37.700572, 55.782945],
        [37.701108, 55.782619],
        [37.701559, 55.782293],
        [37.701988, 55.781907],
        [37.70231, 55.781497],
        [37.702701, 55.780744],
        [37.702923, 55.780319],
        [37.702923, 55.780319],
        [37.703403, 55.779367],
        [37.703404, 55.779366],
        [37.703514, 55.779162],
        [37.703604, 55.778945],
        [37.703675, 55.778685],
        [37.7037, 55.778436],
        [37.703694, 55.778202],
        [37.703656, 55.778037],
        [37.703569, 55.777823],
        [37.702531, 55.775537],
        [37.70253, 55.775534],
        [37.702406, 55.77513],
        [37.702405, 55.775125],
        [37.702368, 55.774763],
        [37.702368, 55.774759],
        [37.702393, 55.774397],
        [37.702393, 55.774395],
        [37.702443, 55.774096],
        [37.702444, 55.774091],
        [37.702568, 55.773764],
        [37.70257, 55.773759],
        [37.702743, 55.773453],
        [37.702745, 55.77345],
        [37.702955, 55.773151],
        [37.702956, 55.77315],
        [37.703241, 55.772775],
        [37.703244, 55.772771],
        [37.704616, 55.771339],
        [37.70512, 55.77071],
        [37.705353, 55.770317],
        [37.705549, 55.769876],
        [37.705608, 55.76965],
        [37.706656, 55.764729],
        [37.706656, 55.764729],
        [37.706702, 55.764514],
        [37.706947, 55.763364],
        [37.706948, 55.763361],
        [37.70714, 55.762818],
        [37.707141, 55.762814],
        [37.707421, 55.762274],
        [37.707426, 55.762267],
        [37.707898, 55.761711],
        [37.707906, 55.761703],
        [37.708495, 55.761263],
        [37.708499, 55.76126],
        [37.709457, 55.760645],
        [37.709464, 55.760641],
        [37.713477, 55.758641],
        [37.715215, 55.757687],
        [37.716322, 55.756921],
        [37.717107, 55.756227],
        [37.717815, 55.755476],
        [37.718364, 55.754801],
        [37.718996, 55.753775],
        [37.718996, 55.753775],
        [37.71912, 55.753575],
        [37.719657, 55.752703],
        [37.720106, 55.751879],
        [37.720106, 55.751878],
        [37.720661, 55.750937],
        [37.721263, 55.749794],
        [37.721501, 55.748815],
        [37.721439, 55.747975],
        [37.721234, 55.747205],
        [37.720722, 55.746381],
        [37.719975, 55.745604],
        [37.71675, 55.742771],
        [37.716748, 55.74277],
        [37.715598, 55.741657],
        [37.715592, 55.74165],
        [37.715077, 55.74085],
        [37.715074, 55.740844],
        [37.714759, 55.739999],
        [37.714758, 55.739991],
        [37.714716, 55.739261],
        [37.714716, 55.739257],
        [37.714798, 55.738538],
        [37.714799, 55.738532],
        [37.715045, 55.737814],
        [37.71505, 55.737806],
        [37.715543, 55.737098],
        [37.71555, 55.737091],
        [37.716311, 55.736418],
        [37.71632, 55.736412],
        [37.717452, 55.735728],
        [37.717461, 55.735723],
        [37.718757, 55.735143],
        [37.718767, 55.735139],
        [37.725074, 55.733125],
        [37.729857, 55.731737],
        [37.729857, 55.731737],
        [37.730402, 55.731578],
        [37.732163, 55.731039],
        [37.734005, 55.730479],
        [37.734553, 55.730273],
        [37.735023, 55.730056],
        [37.735419, 55.729817],
        [37.735729, 55.729505],
        [37.73601, 55.729182],
        [37.736235, 55.728862],
        [37.736449, 55.728463],
        [37.736599, 55.728101],
        [37.7366, 55.728091],
        [37.736644, 55.727785],
        [37.73662, 55.727385],
        [37.736509, 55.72701],
        [37.736304, 55.726571],
        [37.735999, 55.726047],
        [37.733896, 55.723146],
        [37.733445, 55.722494],
        [37.732973, 55.721998],
        [37.728832, 55.718663],
        [37.728467, 55.718264],
        [37.728252, 55.71795],
        [37.728124, 55.717575],
        [37.728016, 55.717188],
        [37.727909, 55.716826],
        [37.727973, 55.716378],
        [37.728252, 55.715436],
        [37.729154, 55.713296],
        [37.729325, 55.712583],
        [37.729261, 55.711942],
        [37.728308, 55.709551],
        [37.728174, 55.709055],
        [37.728144, 55.708544],
        [37.728267, 55.707331],
      ],
    } as PolylineOptions,
  },
  {
    name: 'Самая глубокая станция',
    tag: 'Транспорт',
    title: 'Самая глубокая станция',
    description: (
      <>
        Петербургскому метрополитену свойственна глубина: из-за подвижных
        водонасыщенных грунтов, на&nbsp;которых стоит город. Вот и&nbsp;самая
        глубокая в&nbsp;России станция метро&nbsp;&mdash; 86&nbsp;метров
        вниз&nbsp;&mdash; находится именно здесь. Это примерно высота
        28&mdash;30-этажного дома.
        <br /> <br />
        &laquo;Адмиралтейскую&raquo; обычно включают в&nbsp;тройку самых глубоко
        залегающих станций мира, вместе с&nbsp;киевской
        &laquo;Арсенальной&raquo; и&nbsp;пхеньянской станцией
        &laquo;Пухын&raquo; (точные параметры которой неизвестны из-за
        закрытости Северной Кореи).
        <br /> <br />
        &laquo;Адмиралтейская&raquo;, как и&nbsp;её&nbsp;ближайший московский
        конкурент &laquo;Парк Победы&raquo; (глубиной
        80&nbsp;метров)&nbsp;&mdash; долгострой: начали в&nbsp;1990-е,
        а&nbsp;закончили уже в&nbsp;XXI&nbsp;веке.
      </>
    ),
    description_mob: (
      <>
        Петербургскому метрополитену свойственна глубина: из-за подвижных
        водонасыщенных грунтов, на&nbsp;которых стоит город. Вот и&nbsp;самая
        глубокая в&nbsp;России станция метро&nbsp;— 86&nbsp;метров вниз&nbsp;—
        находится именно здесь. Это примерно высота 28—30-этажного дома.
      </>
    ),
    address: '«Адмиралтейская» в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/v3a31881',
    map: {
      center: [30.31460572675359, 59.93592648206267],
      zoom: 18,
    },
    selectObjects: ['5348660212813041'],
    markerCords: [30.314917, 59.935891],
  },
  {
    name: 'Самый длинный перегон',
    tag: 'Транспорт',
    title: 'Самый длинный перегон',
    description: (
      <>
        &laquo;Давай раньше выйдем и&nbsp;прогуляемся?&raquo;. Эта фраза,
        наверное, реже всего звучит на&nbsp;московских станциях
        &laquo;Строгино&raquo; и&nbsp;&laquo;Крылатское&raquo; синей ветки,
        потому что между ними&nbsp;&mdash; самый длинный в&nbsp;столице
        и&nbsp;вообще в&nbsp;России перегон: 6,6&nbsp;км.
        <br /> <br />
        На&nbsp;втором месте по&nbsp;длине&nbsp;&mdash; перегон между
        &laquo;Обухово&raquo; и&nbsp;конечной &laquo;Рыбацкое&raquo; третьей
        ветки санкт-петербургского метрополитена, его длина&nbsp;&mdash;
        3,6&nbsp;км. На&nbsp;третьем&nbsp;&mdash; перегон между
        &laquo;Московской&raquo; и&nbsp;конечной &laquo;Горьковской&raquo;
        Автозаводской линии нижегородского метрополитена: 3,2&nbsp;км,
        из&nbsp;которых 1,2&nbsp;км&nbsp;&mdash; метромост.
        <br /> <br />
        И&nbsp;это далеко не&nbsp;самый длинный метромост в&nbsp;России, самый
        длинный&nbsp;&mdash; вообще во&nbsp;всем мире,&nbsp;&mdash;
        по&nbsp;данным Книги рекордов Гиннесса, находится в&nbsp;Новосибирске.
        Длина семипролётного моста через Обь&nbsp;&mdash; 2,1&nbsp;км.
      </>
    ),
    description_mob: (
      <>
        «Давай раньше выйдем и&nbsp;прогуляемся?». Эта фраза, наверное, реже
        всего звучит на&nbsp;московских станциях «Строгино» и&nbsp;«Крылатское»
        синей ветки, потому что между ними&nbsp;— самый длинный в&nbsp;столице
        и&nbsp;вообще в&nbsp;России перегон: 6,6&nbsp;км.
      </>
    ),
    address: '«Строгино» - «Крылатское» в Москве',
    href: 'https://2gis.onelink.me/nw2S/ndr06bbq',
    map: {
      center: [37.38405584750637, 55.7798641007386],
      zoom: 12.5,
    },
    polyline: {
      coordinates: [
        [37.403157, 55.803783],
        [37.406127, 55.80315],
        [37.409972, 55.802214],
        [37.411211, 55.801833],
        [37.41234, 55.801226],
        [37.412876, 55.800461],
        [37.41308, 55.799715],
        [37.412846, 55.798903],
        [37.412427, 55.798066],
        [37.410376, 55.795774],
        [37.409308, 55.79495],
        [37.407758, 55.794321],
        [37.402787, 55.793369],
        [37.401872, 55.793017],
        [37.401209, 55.792548],
        [37.399063, 55.790045],
        [37.397986, 55.789362],
        [37.396769, 55.788946],
        [37.394649, 55.788544],
        [37.39328, 55.788207],
        [37.392392, 55.787821],
        [37.391718, 55.787369],
        [37.391376, 55.786947],
        [37.391205, 55.786483],
        [37.391194, 55.785935],
        [37.39128, 55.785434],
        [37.391472, 55.784988],
        [37.391772, 55.784543],
        [37.392074, 55.784193],
        [37.393928, 55.781814],
        [37.39512, 55.780439],
        [37.396508, 55.77904],
        [37.398233, 55.777555],
        [37.400252, 55.77614],
        [37.402159, 55.774994],
        [37.404682, 55.773669],
        [37.40528, 55.773382],
        [37.405874, 55.773009],
        [37.406336, 55.772559],
        [37.406645, 55.772069],
        [37.406785, 55.771563],
        [37.406785, 55.771175],
        [37.406687, 55.770701],
        [37.406504, 55.770298],
        [37.406419, 55.769895],
        [37.406388, 55.769502],
        [37.407951, 55.758632],
        [37.408243, 55.756561],
      ],
    } as PolylineOptions,
  },
  {
    name: 'Самый короткий перегон',
    tag: 'Транспорт',
    title: 'Самый короткий перегон',
    description: (
      <>
        Самый короткий перегон в&nbsp;московском метро&nbsp;&mdash;
        на&nbsp;Филёвской линии: ехать от&nbsp;станции &laquo;Выставочная&raquo;
        до&nbsp;&laquo;Международной&raquo;&nbsp;&mdash; всего 1&nbsp;минуту,
        498&nbsp;метров.
        <br /> <br />
        Обе станции находятся на&nbsp;территории Москва-Сити, так что если
        спешите в&nbsp;один из&nbsp;небоскребов не&nbsp;всегда стоит ждать поезд
        назад, если проскочили нужную станцию. Пешком получится возможно
        быстрее!
      </>
    ),
    description_mob: (
      <>
        Самый короткий перегон в&nbsp;московском метро&nbsp;— на&nbsp;Филёвской
        линии: ехать от&nbsp;станции «Москва-Сити» до&nbsp;«Делового
        центра»&nbsp;— всего 1&nbsp;минуту, 498&nbsp;метров. Обе станции
        находятся на&nbsp;территории Москва-Сити.
      </>
    ),
    address: '«Выставочная» - «Международная» в Москве',
    href: 'https://2gis.onelink.me/nw2S/vlh7hchm',
    map: {
      center: [37.53624997561607, 55.74885877326977],
      zoom: 16,
    },
    polyline: {
      coordinates: [
        [37.540569, 55.750117],
        [37.540041, 55.749947],
        [37.539972, 55.749925],
        [37.540004, 55.749894],
        [37.539516, 55.749737],
        [37.539539, 55.749745],
        [37.539085, 55.749599],
        [37.539053, 55.74963],
        [37.537953, 55.749276],
        [37.537743, 55.749209],
        [37.536598, 55.748841],
        [37.536553, 55.748826],
        [37.536479, 55.748798],
        [37.536408, 55.748767],
        [37.536341, 55.748732],
        [37.53628, 55.748695],
        [37.536235, 55.748662],
        [37.536225, 55.748655],
        [37.536175, 55.748613],
        [37.536131, 55.748568],
        [37.536093, 55.748522],
        [37.536081, 55.748503],
        [37.536056, 55.748462],
        [37.535502, 55.748468],
        [37.535139, 55.748471],
        [37.533838, 55.748485],
      ],
    } as PolylineOptions,
  },
  {
    name: 'Самая большая парковка',
    tag: 'Место',
    title: 'Самая большая парковка',
    description: (
      <>
        Уличная парковка ТК&nbsp;&laquo;Садовод&raquo; в&nbsp;Москве готова
        вместить 10&nbsp;700&nbsp;машин. Неудивительно, по&nbsp;площади
        &laquo;Садовод&raquo;&nbsp;&mdash; один из&nbsp;самых крупных торговых
        комплексов России&nbsp;&mdash; более 40&nbsp;га. С&nbsp;ней сопоставима
        только парковка ТРК OZ&nbsp;MALL&nbsp;на окраине Краснодара:
        7200&nbsp;машиномест, но&nbsp;на&nbsp;всякий случай рядом есть ещё
        одна&nbsp;&mdash; на&nbsp;1200&nbsp;мест.
      </>
    ),
    description_mob: (
      <>
        Уличная парковка ТК&nbsp;«Садовод» в&nbsp;Москве готова вместить
        10&nbsp;700&nbsp;машин. Неудивительно, по&nbsp;площади «Садовод»&nbsp;—
        один из&nbsp;самых крупных торговых комплексов России&nbsp;— более
        40&nbsp;га.
      </>
    ),
    address: 'Около ТК «Садовод»',
    href: 'https://2gis.onelink.me/nw2S/7r86s9an',
    map: {
      center: [37.830156, 55.655382],
      zoom: 16.3,
    },
    selectObjects: ['4504656189325449'],
    markerCords: [37.830059, 55.656375],
  },
  {
    name: 'Самое гостеприимное здание',
    tag: 'Здание',
    title: 'Самое гостеприимное здание',
    description: (
      <>
        У&nbsp;калининградского центрального рынка на&nbsp;улице
        Черняховского&nbsp;&mdash; 241&nbsp;вход. Он&nbsp;оставляет далеко
        позади московскую &laquo;Мега Белая Дача&raquo; с&nbsp;191 входом
        и&nbsp;петербургский &laquo;Outlet Village Пулково&raquo;
        с&nbsp;149&nbsp;входами.
        <br />
        <br />
        Посчитать входы и&nbsp;выходы&nbsp;&mdash; не&nbsp;единственный повод
        посетить центральный рынок, если вы&nbsp;вдруг окажетесь
        в&nbsp;Калининграде: советуем закупиться здесь одним из&nbsp;главных
        местных &laquo;шпециалитетов&raquo;&nbsp;&mdash; копчёным угрём.
      </>
    ),
    description_mob: (
      <>
        У&nbsp;калининградского центрального рынка на&nbsp;улице
        Черняховского&nbsp;— 241&nbsp;вход. Он&nbsp;оставляет далеко позади
        московскую «Мега Белая Дача» с&nbsp;191 входом и&nbsp;петербургский
        «Outlet Village Пулково» с&nbsp;149&nbsp;входами.
      </>
    ),
    address: 'Центральный рынок в Калининграде',
    href: 'https://2gis.onelink.me/nw2S/p0dvcrqj',
    map: {
      center: [20.510015, 54.720606],
      zoom: 16.6,
      pitch: 3.98,
      rotation: -5.53,
    },
    selectObjects: ['5630135189374069', '20.510015', '54.72060599999998'],
    markerCords: [37.64814, 55.537246],
  },
  {
    name: 'Самая маленькая площадь',
    tag: 'Место',
    title: 'Самая маленькая площадь',
    description: (
      <>
        Самая маленькая площадь России&nbsp;&mdash; Флигельная
        в&nbsp;Санкт-Петербурге. Так утверждает знак на&nbsp;доме
        38&nbsp;по&nbsp;3-й линии Васильевского острова. По&nbsp;сути Флигельная
        площадь&nbsp;&mdash; небольшой внутренний двор, официально
        не&nbsp;зарегистрированный топонимической комиссией города, но&nbsp;уже
        ставший известной неформальной достопримечательностью Петербурга.
        И&nbsp;даже пользователи 2ГИС в&nbsp;отзывах считают, что
        &laquo;не&nbsp;очень похожа на&nbsp;какую-то площадь&raquo;.
        <br />
        <br />
        Другой похожий пример&nbsp;&mdash; Рыбнорядская площадь в&nbsp;Казани,
        которая тоже больше похожа на&nbsp;внутренний двор. Зато внутри, как
        говорят пользователи, можно поймать атмосферу старой Казани.
      </>
    ),
    description_mob: (
      <>
        Самая маленькая площадь России&nbsp;— Флигельная
        в&nbsp;Санкт-Петербурге. Так утверждает знак на&nbsp;доме
        38&nbsp;по&nbsp;3-й линии Васильевского острова. По&nbsp;сути Флигельная
        площадь&nbsp;— небольшой внутренний двор, официально
        не&nbsp;зарегистрированный топонимической комиссией.
      </>
    ),
    address: 'Флигельная площадь в Санкт-Петербурге',
    href: 'https://2gis.onelink.me/nw2S/0helpj95',
    map: {
      center: [30.281275, 59.94521],
      zoom: 19,
      pitch: 3.98,
      rotation: 19.23,
    },
    selectObjects: ['5348711752271518'],
    polygon: {
      coordinates: [
        [
          [30.281288, 59.945198],
          [30.281395, 59.945105],
          [30.281581, 59.945159],
          [30.281474, 59.945252],
          [30.281288, 59.945198],
        ],
      ],
    },
    markerCords: [30.281382, 59.945174],
  },
  {
    name: 'Самый большой ТЦ',
    tag: 'Здание',
    title: 'Самый большой ТЦ',
    description: (
      <>
        Торгово-развлекательный центр &laquo;Авиапарк&raquo; одно время был даже
        самым большим торговым центром в&nbsp;Европе. Его общая площадь
        составляет 400&nbsp;000&nbsp;м&sup2;, торговая площадь&nbsp;&mdash;
        230&nbsp;000&nbsp;м&sup2;. По&nbsp;данным 2ГИС, в&nbsp;торговом
        центре&nbsp;&mdash; более 900&nbsp;организаций, четыре этажа и&nbsp;два
        уровня парковки. Среди торговых комплексов лидирует
        &laquo;Садовод&raquo;&nbsp;&mdash; его общая площадь
        400&nbsp;000&nbsp;м&sup2;.
      </>
    ),
    description_mob: (
      <>
        Торгово-развлекательный центр «Авиапарк» одно время был даже самым
        большим торговым центром в&nbsp;Европе. Его общая площадь составляет
        400&nbsp;000&nbsp;м², торговая площадь&nbsp;— 230&nbsp;000&nbsp;м².
        По&nbsp;данным 2ГИС, в&nbsp;торговом центре&nbsp;— более
        900&nbsp;организаций, четыре этажа и&nbsp;два уровня парковки. Среди
        торговых комплексов лидирует «Садовод»&nbsp;— его общая площадь
        400&nbsp;000&nbsp;м².
      </>
    ),
    address: 'Авиапарк в Москве',
    href: 'https://2gis.onelink.me/nw2S/zmmfnvph',
    map: {
      center: [37.529895, 55.790473],
      zoom: 17.01,
      pitch: 50,
      rotation: -63.13,
    },
    selectObjects: ['4504235283134799', '37.529895', '55.790473'],
    markerCords: [37.529729, 55.790627],
  },
  {
    name: 'Самая высокая смотровая',
    tag: 'Место',
    title: 'Самая высокая смотровая',
    description: (
      <>
        В&nbsp;Останкинской башне можно побывать на&nbsp;смотровых площадках
        на&nbsp;высоте 340 метров (работает с&nbsp;мая по&nbsp;октябрь
        в&nbsp;хорошую погоду) или на&nbsp;высоте 337&nbsp;метров. В&nbsp;МФК
        &laquo;Федерация&raquo; можно подняться на&nbsp;смотровую площадку
        на&nbsp;высоте 327 метров (89&nbsp;этаж). Но&nbsp;рекорд&nbsp;&mdash;
        в&nbsp;Москве, России и&nbsp;Европе&nbsp;&mdash; у&nbsp;смотровой
        площадки &laquo;Выше только любовь&raquo; в&nbsp;башне ОКО
        в&nbsp;Москва-Сити&nbsp;&mdash; 354&nbsp;метра, 92&nbsp;этаж.
      </>
    ),
    description_mob: (
      <>
        Рекорд&nbsp;— в&nbsp;Москве, России и&nbsp;Европе&nbsp;—
        у&nbsp;смотровой площадки «Выше только любовь» в&nbsp;башне ОКО
        в&nbsp;Москва-Сити&nbsp;— 354&nbsp;метра, 92&nbsp;этаж (если судить
        по&nbsp;информации на&nbsp;официальных сайтах московских смотровых
        площадок).
      </>
    ),
    address: '«Выше только любовь» в башне ОКО в Москве',
    href: 'https://2gis.onelink.me/nw2S/azgomvyq',
    map: {
      center: [37.53498, 55.74932],
      zoom: 17.01,
      pitch: 50,
      rotation: -63.13,
    },
    selectObjects: ['4504235316469867', '37.534921', '55.749361'],
    markerCords: [37.53407, 55.749932],
  },
  {
    name: 'Самое рекордное место',
    tag: 'Город',
    title: 'Самое рекордное место',
    description: (
      <>
        На&nbsp;карте рекордов мы&nbsp;просто не&nbsp;смогли обойти этот объект
        стороной. Здесь рекордное все&nbsp;&mdash; и&nbsp;набережная, и&nbsp;Дом
        культуры, и&nbsp;даже улица Центральная, уверены, здесь самая рекордная.
      </>
    ),
    description_mob: (
      <>
        На&nbsp;карте рекордов мы&nbsp;просто не&nbsp;смогли обойти этот объект
        стороной. Здесь рекордное все&nbsp;— и&nbsp;набережная, и&nbsp;Дом
        культуры, и&nbsp;даже улица Центральная, уверены, здесь самая рекордная.
      </>
    ),
    address: 'село Рекорд, Саратовская область',
    href: 'https://2gis.onelink.me/nw2S/4dnrezim',
    map: {
      center: [46.66541, 50.9618],
      zoom: 15.38,
    },
    selectObjects: ['70030076127914241'],
    markerCords: [46.66541, 50.9618],
  },
]
